import React, { useState, useEffect } from 'react';
import './CommissionerPage.css';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import { SUPABASE_URL, SUPABASE_ANON_KEY, FUNCTIONS_URL } from '../services/supabaseConfig';
import { getItem, setItem, StoredPlayer, StoredUser, StoredTeam, getCurrentSeason } from '../services/localStorage';
import { 
  gameDayToShortDate,
} from '../utils/dateUtils';
import { 
  FORGE_CONFIG, 
  ForgeConfig, 
  TIER_NAMES, 
  TIER_COLORS,
  SimulationConfig,
  DEFAULT_SIMULATION_CONFIG
} from '../config/gameConfig';
import { generateRandomFace } from '../utils/faceGenerator';
import { numberToGrade } from '../utils/grades';
import { LEAGUE_TYPE, QL_ARRAY, TRAD_ARRAY } from '../constants/leagueTypes';
import { 
  StoredLeague, 
  StoredLeagueSet,
  StoredSimulationState,
  createLeague, 
  getAllLeagues, 
  getAllLeagueSets,
  getSimulationState,
  startSimulation,
  stopSimulation,
  setSimulationSpeed,
  advanceSimulationDay,
  updateLeagueStatus,
  updateSimulationState
} from '../services/db';
import { 
  advanceToDayOne, 
  checkAndFillRosters,
  getCurrentDay 
} from '../services/SeasonManagerService';
import { 
  simulateDay, 
  isRegularSeasonComplete,
  startPlayoffs,
  getCurrentSimulationDay,
  instantSimulate,
  simulateToPlayoffs
} from '../services/GameSimulationService';



// ─────────────────────────────────────────────────────────────────────────────
// Season Transition Wizard
// ─────────────────────────────────────────────────────────────────────────────

type LeagueAction = 'advance' | 'keep' | 'delete';

interface LeaguePlan {
  id: string;
  name: string;
  tier: number;
  status: string;
  season_year?: number;
  sim_date?: string;
  is_ai_run?: boolean;
  action: LeagueAction;
  newTier: number; // may differ from tier if promote/relegate
  teamMoves: TeamMove[];
}

interface TeamMove {
  teamId: string;
  teamName: string;
  teamCity: string;
  fromLeagueId: string;
  toLeagueId: string;
}

const TIER_LABEL: Record<number, string> = {
  1: 'MLB',
  2: 'AAA',
  3: 'AA',
  4: 'Rookie',
  5: 'Dev',
};

const TIER_COLOR: Record<number, string> = {
  1: '#f59e0b',
  2: '#818cf8',
  3: '#34d399',
  4: '#60a5fa',
  5: '#94a3b8',
};

type WizardStep = 'plan' | 'team-moves' | 'review' | 'executing' | 'done';

const SeasonTransitionWizard: React.FC<{
  leagues: any[];
  supabase: any;
  FUNCTIONS_URL: string;
  onDone: () => void;
  setMessage: (m: string) => void;
}> = ({ leagues, supabase, FUNCTIONS_URL, onDone, setMessage }) => {
  const [step, setStep] = useState<WizardStep>('plan');
  const [plans, setPlans] = useState<LeaguePlan[]>([]);
  const [allTeams, setAllTeams] = useState<any[]>([]);
  const [log, setLog] = useState<string[]>([]);
  const [moveLeague, setMoveLeague] = useState<string | null>(null); // which league is open for team moves
  const [executingIdx, setExecutingIdx] = useState(0);
  const logRef = React.useRef<HTMLDivElement>(null);

  // Init plans from leagues — only when we have data AND plans is empty (don't overwrite user edits)
  React.useEffect(() => {
    if (leagues.length === 0) return;
    // Only initialize if we have no plans yet, or if the league set has changed (different IDs)
    const currentIds = plans.map(p => p.id).sort().join(',');
    const incomingIds = leagues.map((l: any) => l.id).sort().join(',');
    if (plans.length > 0 && currentIds === incomingIds) return; // don't overwrite user edits
    setPlans(leagues.map((l: any) => ({
      id: l.id,
      name: l.name,
      tier: l.tier ?? 1,
      status: l.status,
      season_year: l.season_year,
      sim_date: l.sim_date,
      is_ai_run: l.is_ai_run,
      action: 'advance' as LeagueAction,
      newTier: l.tier ?? 1,
      teamMoves: [],
    })));
  }, [leagues]); // eslint-disable-line

  // Load teams once
  React.useEffect(() => {
    if (!supabase) return;
    supabase.from('teams').select('id, name, city, league_id, abbreviation').then(({ data }: any) => {
      setAllTeams(data || []);
    });
  }, [supabase]);

  const addLog = (line: string) => {
    setLog(prev => [...prev, line]);
    setTimeout(() => logRef.current?.scrollTo(0, logRef.current.scrollHeight), 50);
  };

  const updatePlan = (id: string, patch: Partial<LeaguePlan>) => {
    setPlans(prev => prev.map(p => p.id === id ? { ...p, ...patch } : p));
  };

  const addTeamMove = (leagueId: string, teamId: string, toLeagueId: string) => {
    const team = allTeams.find(t => t.id === teamId);
    if (!team) return;
    setPlans(prev => prev.map(p => {
      if (p.id === leagueId) {
        // Remove any existing move for this team
        const existing = p.teamMoves.filter(m => m.teamId !== teamId);
        if (toLeagueId === leagueId) return { ...p, teamMoves: existing }; // same league = remove
        return { ...p, teamMoves: [...existing, { teamId, teamName: team.name, teamCity: team.city, fromLeagueId: leagueId, toLeagueId }] };
      }
      return p;
    }));
  };

  const removeTeamMove = (leagueId: string, teamId: string) => {
    setPlans(prev => prev.map(p =>
      p.id === leagueId ? { ...p, teamMoves: p.teamMoves.filter(m => m.teamId !== teamId) } : p
    ));
  };

  // ── EXECUTE ──────────────────────────────────────────────────────────────
  const execute = async () => {
    setStep('executing');
    setLog([]);
    let idx = 0;

    const token = (await supabase.auth.getSession()).data.session?.access_token;

    for (const plan of plans) {
      setExecutingIdx(idx++);

      // ── DELETE ──────────────────────────────────────────────────────
      if (plan.action === 'delete') {
        addLog(`🗑️  Deleting ${plan.name}...`);
        try {
          await supabase.from('teams').update({ league_id: null, conference: null, division: null }).eq('league_id', plan.id);
          await supabase.from('league_standings').delete().eq('league_id', plan.id);
          await supabase.from('league_schedule').delete().eq('league_id', plan.id);
          await supabase.from('playoff_brackets').delete().eq('league_id', plan.id);
          await supabase.from('playoff_series').delete().eq('league_id', plan.id);
          await supabase.from('playoff_games').delete().eq('league_id', plan.id);
          await supabase.from('leagues').delete().eq('id', plan.id);
          addLog(`   ✅ Deleted ${plan.name}`);
        } catch (e: any) {
          addLog(`   ❌ Failed to delete ${plan.name}: ${e.message}`);
        }
        continue;
      }

      // ── TEAM MOVES (before season flip) ────────────────────────────
      for (const move of plan.teamMoves) {
        const targetLeague = plans.find(p => p.id === move.toLeagueId);
        if (!targetLeague) continue;
        addLog(`🔀 Moving ${move.teamCity} ${move.teamName} → ${targetLeague.name}...`);
        try {
          const { data: existingTeams } = await supabase.from('teams').select('conference, division').eq('league_id', move.toLeagueId);
          const count = existingTeams?.length || 0;
          let conference = 'American', division = 'East';
          if (count >= 5 && count < 10) { conference = 'American'; division = 'West'; }
          else if (count >= 10 && count < 15) { conference = 'National'; division = 'East'; }
          else if (count >= 15) { conference = 'National'; division = 'West'; }
          await supabase.from('teams').update({
            league_id: move.toLeagueId,
            tier: targetLeague.newTier,
            conference, division,
          }).eq('id', move.teamId);
          await supabase.from('league_standings').delete().eq('team_id', move.teamId);
          await supabase.from('league_standings').insert({
            league_id: move.toLeagueId, team_id: move.teamId,
            wins: 0, losses: 0, runs_scored: 0, runs_allowed: 0,
            conference, division,
          });
          addLog(`   ✅ Moved to ${targetLeague.name} (${conference} ${division})`);
        } catch (e: any) {
          addLog(`   ❌ Failed: ${e.message}`);
        }
      }

      // ── ADVANCE (keep = skip flip, advance = call start-next-season) ─
      if (plan.action === 'advance') {
        addLog(`⏭️  Advancing ${plan.name} to next season...`);
        try {
          const res = await fetch(`${FUNCTIONS_URL}/start-next-season`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
              'x-admin-secret': 'simforge-admin-2024',
            },
            body: JSON.stringify({ leagueId: plan.id }),
          });
          const data = await res.json();
          if (data.error) {
            addLog(`   ❌ ${data.error}`);
          } else {
            addLog(`   ✅ Season ${(plan.season_year ?? 1900) + 1} ready`);
          }
        } catch (e: any) {
          addLog(`   ❌ ${e.message}`);
        }
      } else {
        addLog(`⏸️  Keeping ${plan.name} in off-season (skipped flip)`);
      }

      // ── TIER CHANGE (runs after season flip so it always wins) ────────────
      if (plan.newTier !== plan.tier) {
        const dir = plan.newTier < plan.tier ? '⬆️ Promoting' : '⬇️ Relegating';
        addLog(`${dir} ${plan.name} → ${TIER_LABEL[plan.newTier] || 'Tier ' + plan.newTier}...`);
        try {
          await supabase.from('leagues').update({ tier: plan.newTier }).eq('id', plan.id);
          await supabase.from('teams').update({ tier: plan.newTier }).eq('league_id', plan.id);
          addLog(`   ✅ Tier updated to ${TIER_LABEL[plan.newTier]}`);
        } catch (e: any) {
          addLog(`   ❌ Tier update failed: ${e.message}`);
        }
      }
    }

    // ── SYNC GLOBAL SIMULATION STATE ───────────────────────────────────────
    const advancedPlans = plans.filter(p => p.action === 'advance');
    if (advancedPlans.length > 0) {
      const maxYear = Math.max(...advancedPlans.map(p => (p.season_year ?? 1900) + 1));
      addLog(`🔄 Syncing global simulation_state to year ${maxYear}, active...`);
      try {
        await supabase.from('simulation_state').update({
          season_status: 'active', current_day: 1,
          season_year: maxYear, sim_date: `${maxYear}-04-01`,
          is_running: true, last_tick_at: new Date().toISOString(),
        }).eq('id', 'global');
        addLog(`   ✅ Global state synced`);
      } catch (e: any) {
        addLog(`   ❌ Global sync failed: ${e.message}`);
      }
    }

    addLog('');
    addLog('🏁 Transition complete!');
    setStep('done');
    onDone();
  };

  // ── SUMMARY COUNTS ────────────────────────────────────────────────────────
  const toAdvance = plans.filter(p => p.action === 'advance').length;
  const toKeep = plans.filter(p => p.action === 'keep').length;
  const toDelete = plans.filter(p => p.action === 'delete').length;
  const totalMoves = plans.reduce((s, p) => s + p.teamMoves.length, 0);
  const tierChanges = plans.filter(p => p.newTier !== p.tier && p.action !== 'delete').length;

  // ── RENDER ────────────────────────────────────────────────────────────────
  const tierGroups = [1, 2, 3, 4, 5].map(t => ({
    tier: t,
    leagues: plans.filter(p => p.tier === t),
  })).filter(g => g.leagues.length > 0);

  const actionColor: Record<LeagueAction, string> = {
    advance: '#22c55e',
    keep: '#f59e0b',
    delete: '#ef4444',
  };

  const sz = { padding: '4px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 };

  return (
    <div className="comm-section">
      <h2>🏁 Season Transition Wizard</h2>
      <p className="section-desc">Review and execute all off-season changes in one place — delete leagues, adjust tiers, move teams, then flip to the next season.</p>

      {/* Step indicator */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {(['plan', 'team-moves', 'review', 'executing', 'done'] as WizardStep[]).map((s, i) => (
          <div key={s} style={{
            padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600,
            background: step === s ? '#e8b82a' : 'rgba(255,255,255,0.05)',
            color: step === s ? '#000' : '#888',
            border: `1px solid ${step === s ? '#e8b82a' : 'rgba(255,255,255,0.1)'}`,
          }}>
            {i + 1}. {s === 'plan' ? 'League Plan' : s === 'team-moves' ? 'Team Moves' : s === 'review' ? 'Review' : s === 'executing' ? 'Executing' : 'Done'}
          </div>
        ))}
      </div>

      {/* ── STEP 1: PLAN ───────────────────────────────────────────────────── */}
      {step === 'plan' && (
        <div>
          <p style={{ color: '#aaa', marginBottom: 16 }}>For each league, choose what to do this off-season. You can also change its tier.</p>
          {tierGroups.map(({ tier, leagues: tl }) => (
            <div key={tier} style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: TIER_COLOR[tier], letterSpacing: 1, textTransform: 'uppercase' }}>
                  {TIER_LABEL[tier] || `Tier ${tier}`}
                </span>
                <div style={{ flex: 1, height: 1, background: `${TIER_COLOR[tier]}33` }} />
              </div>
              {tl.map(plan => (
                <div key={plan.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                  padding: '10px 14px', marginBottom: 8, borderRadius: 10,
                  background: 'rgba(255,255,255,0.03)',
                  border: `1px solid ${plan.action === 'delete' ? '#ef444433' : plan.action === 'keep' ? '#f59e0b33' : '#22c55e22'}`,
                }}>
                  {/* League name + status */}
                  <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>{plan.name}</div>
                    <div style={{ fontSize: 12, color: '#888' }}>
                      Season {plan.season_year ?? '?'} · {plan.sim_date ?? 'off-season'} · {plan.is_ai_run ? '🤖 AI' : '👤 Human'}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div style={{ display: 'flex', gap: 6 }}>
                    {(['advance', 'keep', 'delete'] as LeagueAction[]).map(a => (
                      <button key={a} style={{ ...sz,
                        background: plan.action === a ? actionColor[a] : 'rgba(255,255,255,0.07)',
                        color: plan.action === a ? '#000' : '#aaa',
                      }} onClick={() => updatePlan(plan.id, { action: a })}>
                        {a === 'advance' ? '⏭️ Advance' : a === 'keep' ? '⏸️ Keep' : '🗑️ Delete'}
                      </button>
                    ))}
                  </div>

                  {/* Tier change (only if not deleting) */}
                  {plan.action !== 'delete' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 12, color: '#888' }}>Tier:</span>
                      {plan.newTier > 1 && (
                        <button style={{ ...sz, background: 'rgba(99,102,241,0.2)', color: '#818cf8' }}
                          onClick={() => updatePlan(plan.id, { newTier: plan.newTier - 1 })}>
                          ⬆️
                        </button>
                      )}
                      <span style={{ fontSize: 13, fontWeight: 700, color: TIER_COLOR[plan.newTier] }}>
                        {TIER_LABEL[plan.newTier] || `T${plan.newTier}`}
                        {plan.newTier !== plan.tier && <span style={{ color: '#f59e0b', marginLeft: 4 }}>(changed)</span>}
                      </span>
                      {plan.newTier < 5 && (
                        <button style={{ ...sz, background: 'rgba(99,102,241,0.2)', color: '#818cf8' }}
                          onClick={() => updatePlan(plan.id, { newTier: plan.newTier + 1 })}>
                          ⬇️
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
          <button className="save-btn" style={{ marginTop: 8 }}
            onClick={() => setStep('team-moves')}>
            Next: Team Moves →
          </button>
        </div>
      )}

      {/* ── STEP 2: TEAM MOVES ─────────────────────────────────────────────── */}
      {step === 'team-moves' && (
        <div>
          <p style={{ color: '#aaa', marginBottom: 16 }}>Move individual teams between leagues. Useful for promotions, relegations, or restructuring. Skip if none needed.</p>

          {plans.filter(p => p.action !== 'delete').map(plan => {
            const leagueTeams = allTeams.filter(t => t.league_id === plan.id);
            const isOpen = moveLeague === plan.id;
            const pendingMoves = plan.teamMoves;

            return (
              <div key={plan.id} style={{
                marginBottom: 12, borderRadius: 10, overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.08)',
              }}>
                {/* League header */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 14px', background: 'rgba(255,255,255,0.04)',
                  cursor: 'pointer',
                }} onClick={() => setMoveLeague(isOpen ? null : plan.id)}>
                  <span style={{ color: TIER_COLOR[plan.newTier], fontWeight: 700 }}>{TIER_LABEL[plan.newTier]}</span>
                  <span style={{ fontWeight: 600 }}>{plan.name}</span>
                  <span style={{ color: '#888', fontSize: 12 }}>{leagueTeams.length} teams</span>
                  {pendingMoves.length > 0 && (
                    <span style={{ marginLeft: 4, background: '#f59e0b', color: '#000', borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
                      {pendingMoves.length} move{pendingMoves.length !== 1 ? 's' : ''}
                    </span>
                  )}
                  <span style={{ marginLeft: 'auto', color: '#888' }}>{isOpen ? '▲' : '▼'}</span>
                </div>

                {/* Team list */}
                {isOpen && (
                  <div style={{ padding: 14 }}>
                    {leagueTeams.length === 0 && <p style={{ color: '#888', fontSize: 13 }}>No teams in this league.</p>}
                    {leagueTeams.map(team => {
                      const existingMove = pendingMoves.find(m => m.teamId === team.id);
                      const targetLeague = existingMove ? plans.find(p => p.id === existingMove.toLeagueId) : null;
                      return (
                        <div key={team.id} style={{
                          display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0',
                          borderBottom: '1px solid rgba(255,255,255,0.05)',
                        }}>
                          <span style={{ flex: 1, fontSize: 14 }}>{team.city} {team.name}</span>
                          {existingMove ? (
                            <>
                              <span style={{ fontSize: 12, color: '#f59e0b' }}>→ {targetLeague?.name ?? existingMove.toLeagueId}</span>
                              <button style={{ ...sz, background: 'rgba(239,68,68,0.2)', color: '#ef4444' }}
                                onClick={() => removeTeamMove(plan.id, team.id)}>✕ Remove</button>
                            </>
                          ) : (
                            <select
                              style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #30363d', background: '#0d1117', color: '#aaa', fontSize: 13 }}
                              defaultValue=""
                              onChange={e => { if (e.target.value) addTeamMove(plan.id, team.id, e.target.value); e.target.value = ''; }}
                            >
                              <option value="">Move to...</option>
                              {plans.filter(p => p.id !== plan.id && p.action !== 'delete').map(p => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                              ))}
                            </select>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button style={{ ...sz, background: 'rgba(255,255,255,0.07)', color: '#aaa', padding: '8px 18px' }}
              onClick={() => setStep('plan')}>← Back</button>
            <button className="save-btn" onClick={() => setStep('review')}>
              Next: Review →
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 3: REVIEW ──────────────────────────────────────────────────── */}
      {step === 'review' && (
        <div>
          <h3 style={{ marginBottom: 16 }}>Review Your Plan</h3>

          {/* Summary chips */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
            <div style={{ padding: '8px 16px', borderRadius: 10, background: 'rgba(34,197,94,0.15)', border: '1px solid #22c55e44', color: '#22c55e', fontWeight: 700 }}>
              ⏭️ {toAdvance} leagues advancing
            </div>
            {toKeep > 0 && (
              <div style={{ padding: '8px 16px', borderRadius: 10, background: 'rgba(245,158,11,0.15)', border: '1px solid #f59e0b44', color: '#f59e0b', fontWeight: 700 }}>
                ⏸️ {toKeep} kept in off-season
              </div>
            )}
            {toDelete > 0 && (
              <div style={{ padding: '8px 16px', borderRadius: 10, background: 'rgba(239,68,68,0.15)', border: '1px solid #ef444444', color: '#ef4444', fontWeight: 700 }}>
                🗑️ {toDelete} leagues deleted
              </div>
            )}
            {tierChanges > 0 && (
              <div style={{ padding: '8px 16px', borderRadius: 10, background: 'rgba(129,140,248,0.15)', border: '1px solid #818cf844', color: '#818cf8', fontWeight: 700 }}>
                🔀 {tierChanges} tier changes
              </div>
            )}
            {totalMoves > 0 && (
              <div style={{ padding: '8px 16px', borderRadius: 10, background: 'rgba(96,165,250,0.15)', border: '1px solid #60a5fa44', color: '#60a5fa', fontWeight: 700 }}>
                👥 {totalMoves} team moves
              </div>
            )}
          </div>

          {/* Detail table */}
          <div style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)', marginBottom: 24 }}>
            {plans.map((plan, i) => (
              <div key={plan.id} style={{
                display: 'flex', gap: 12, alignItems: 'flex-start',
                padding: '12px 16px',
                background: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
                borderBottom: i < plans.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
              }}>
                <div style={{ flex: '1 1 200px' }}>
                  <span style={{ fontWeight: 600 }}>{plan.name}</span>
                  <span style={{ marginLeft: 8, fontSize: 12, color: '#888' }}>Season {plan.season_year ?? '?'}</span>
                </div>
                <div style={{ minWidth: 100 }}>
                  <span style={{ fontWeight: 700, color: actionColor[plan.action] }}>
                    {plan.action === 'advance' ? '⏭️ Advance' : plan.action === 'keep' ? '⏸️ Keep' : '🗑️ Delete'}
                  </span>
                </div>
                <div style={{ minWidth: 80, fontSize: 13 }}>
                  {plan.action !== 'delete' && plan.newTier !== plan.tier && (
                    <span style={{ color: '#818cf8' }}>
                      {TIER_LABEL[plan.tier]} → {TIER_LABEL[plan.newTier]}
                    </span>
                  )}
                </div>
                <div style={{ flex: 1, fontSize: 12, color: '#60a5fa' }}>
                  {plan.teamMoves.map(m => {
                    const target = plans.find(p => p.id === m.toLeagueId);
                    return <div key={m.teamId}>👤 {m.teamCity} {m.teamName} → {target?.name ?? m.toLeagueId}</div>;
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Warning for deletes */}
          {toDelete > 0 && (
            <div style={{ padding: 14, borderRadius: 10, background: 'rgba(239,68,68,0.1)', border: '1px solid #ef444433', color: '#fca5a5', marginBottom: 20, fontSize: 13 }}>
              ⚠️ <strong>Deleted leagues are permanent.</strong> Teams will be unassigned and all schedules, standings, and game data will be wiped. Make sure you've moved any teams you want to keep first.
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button style={{ ...sz, background: 'rgba(255,255,255,0.07)', color: '#aaa', padding: '8px 18px' }}
              onClick={() => setStep('team-moves')}>← Back</button>
            <button
              className="save-btn"
              style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: '#fff', padding: '10px 28px', fontWeight: 700, fontSize: 15 }}
              onClick={execute}>
              🚀 Execute Transition
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 4: EXECUTING ───────────────────────────────────────────────── */}
      {(step === 'executing' || step === 'done') && (
        <div>
          <h3 style={{ marginBottom: 16, color: step === 'done' ? '#22c55e' : '#e8b82a' }}>
            {step === 'done' ? '✅ Transition Complete!' : `⏳ Executing... (${executingIdx + 1}/${plans.length})`}
          </h3>
          <div
            ref={logRef}
            style={{
              fontFamily: 'monospace', fontSize: 13, lineHeight: 1.8,
              background: '#0d1117', border: '1px solid #30363d', borderRadius: 10,
              padding: '16px 20px', maxHeight: 400, overflowY: 'auto',
              marginBottom: 20, color: '#e2e8f0',
            }}
          >
            {log.map((line, i) => (
              <div key={i} style={{ color: line.includes('✅') ? '#22c55e' : line.includes('❌') ? '#ef4444' : line.includes('🏁') ? '#e8b82a' : '#e2e8f0' }}>
                {line || '\u00a0'}
              </div>
            ))}
            {step === 'executing' && <div style={{ color: '#e8b82a' }}>▋</div>}
          </div>
          {step === 'done' && (
            <button className="save-btn" onClick={() => {
              setStep('plan');
              setLog([]);
            }}>
              🔄 Start Another Transition
            </button>
          )}
        </div>
      )}
    </div>
  );
};

interface CommissionerPageProps {
  onNavigate?: (page: string) => void;
  userData?: any;
}

const CommissionerPage: React.FC<CommissionerPageProps> = ({ onNavigate, userData }) => {
  const [activeTab, setActiveTab] = useState<'config' | 'players' | 'teams' | 'balls' | 'leagues' | 'simulation' | 'tier-salaries' | 'salary-cap' | 'commissioners' | 'generate' | 'team-queue' | 'sim-log' | 'season-transition'>('config');

  // Handle navigation to other pages
  const handleNavigate = (page: string) => {
    if (onNavigate) {
      onNavigate(page);
    }
  };
  const [config, setConfig] = useState<ForgeConfig>(FORGE_CONFIG);
  const [users, setUsers] = useState<any[]>([]);
  const [players, setPlayers] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<any>(null);
  const [message, setMessage] = useState('');
  const [newCommissionerEmail, setNewCommissionerEmail] = useState('');

  // League state
  const [leagues, setLeagues] = useState<StoredLeague[]>([]);
  const [leagueSets, setLeagueSets] = useState<StoredLeagueSet[]>([]);
  const [newLeagueName, setNewLeagueName] = useState('');
  const [newLeagueTier, setNewLeagueTier] = useState<1 | 2 | 3 | 4 | 5>(3);
  const [newLeagueAiRun, setNewLeagueAiRun] = useState(false);
  const [newLeagueType, setNewLeagueType] = useState<string>(LEAGUE_TYPE.TRADITIONAL);
  const [createLeagueLoading, setCreateLeagueLoading] = useState(false);
  
  // Sim tick log state
  const [simLogs, setSimLogs] = useState<any[]>([]);
  const [simLogLoading, setSimLogLoading] = useState(false);

  // Simulation state
  const [simState, setSimState] = useState<StoredSimulationState>({
    id: 'global',
    is_running: false,
    current_day: 0,
    speed: 'normal',
    last_updated: new Date().toISOString()
  });
  const [currentSeason, setCurrentSeason] = useState<any>(null);

  // Auto-tick: advance day every X minutes
  const [autoTickMinutes, setAutoTickMinutes] = useState(5);
  const [autoTickActive, setAutoTickActive] = useState(false);
  const [nextTickIn, setNextTickIn] = useState(0);
  const autoTickRef = React.useRef<any>(null);
  const countdownRef = React.useRef<any>(null);

  const advanceOneDay = React.useCallback(async () => {
    const currentDay = await getCurrentDay();
    if (currentDay === 0) {
      const result = await advanceToDayOne();
      loadSimulationState();
      setMessage(result.message);
    } else {
      setMessage(`⚾ Simulating Day ${currentDay}...`);
      try {
        const simResult = await simulateDay(currentDay);
        const leagues = await getAllLeagues();
        for (const league of leagues) {
          const complete = await isRegularSeasonComplete(league.id);
          if (complete && league.status === 'active') {
            await startPlayoffs(league.id);
            await updateLeagueStatus(league.id, 'completed');
            const { archiveSeason } = await import('../services/SeasonHistoryService');
            await archiveSeason(league.id, new Date().getFullYear());
          }
        }
        const newDay = currentDay + 1;
        if (newDay === 7) {
          const rosterResult = await checkAndFillRosters();
          if (rosterResult.filled > 0) {
            setMessage(prev => prev + ` Filled ${rosterResult.filled} roster spots.`);
          }
        }
        await advanceSimulationDay();
        loadSimulationState();
        setMessage(`✅ Day ${currentDay}: ${simResult.gamesPlayed} games → Day ${newDay}`);
      } catch (error) {
        console.error('Simulation error:', error);
        setMessage(`❌ Error: ${error}`);
      }
    }
  }, []);

  const startAutoTick = React.useCallback((minutesOverride?: number) => {
    const speedMap: Record<string, number> = { slow: 10, normal: 5, fast: 1, instant: 0.17 };
    const mins = minutesOverride || speedMap[simState.speed] || 5;
    setAutoTickActive(true);
    setAutoTickMinutes(mins);
    const ms = mins * 60 * 1000;
    // Store in localStorage so we can resume after page reload
    localStorage.setItem('autoTickMinutes', String(mins));
    localStorage.setItem('autoTickNextRun', String(Date.now())); // first tick immediately
    // Run first tick immediately
    advanceOneDay();
    localStorage.setItem('autoTickNextRun', String(Date.now() + ms));
    setNextTickIn(mins * 60);
    // Then repeat
    if (autoTickRef.current) clearInterval(autoTickRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    autoTickRef.current = setInterval(() => {
      advanceOneDay();
      localStorage.setItem('autoTickNextRun', String(Date.now() + ms));
      setNextTickIn(mins * 60);
    }, ms);
    // Countdown timer
    countdownRef.current = setInterval(() => {
      const nextRun = parseInt(localStorage.getItem('autoTickNextRun') || '0');
      const remaining = Math.max(0, Math.ceil((nextRun - Date.now()) / 1000));
      setNextTickIn(remaining);
    }, 1000);
  }, [autoTickMinutes, advanceOneDay]);

  // Resume auto-tick on mount if it was running
  useEffect(() => {
    const storedMinutes = localStorage.getItem('autoTickMinutes');
    const storedNextRun = localStorage.getItem('autoTickNextRun');
    if (storedMinutes && storedNextRun) {
      const mins = parseInt(storedMinutes);
      const nextRun = parseInt(storedNextRun);
      const now = Date.now();
      if (nextRun > 0) {
        // Resume — run immediately if overdue, otherwise wait for remaining time
        setAutoTickActive(true);
        setAutoTickMinutes(mins);
        const ms = mins * 60 * 1000;
        const remaining = Math.max(0, nextRun - now);
        setNextTickIn(Math.ceil(remaining / 1000));
        // Run after remaining time, then repeat
        const timeout = setTimeout(() => {
          advanceOneDay();
          localStorage.setItem('autoTickNextRun', String(Date.now() + ms));
          setNextTickIn(mins * 60);
          autoTickRef.current = setInterval(() => {
            advanceOneDay();
            localStorage.setItem('autoTickNextRun', String(Date.now() + ms));
            setNextTickIn(mins * 60);
          }, ms);
          countdownRef.current = setInterval(() => {
            const nr = parseInt(localStorage.getItem('autoTickNextRun') || '0');
            const rem = Math.max(0, Math.ceil((nr - Date.now()) / 1000));
            setNextTickIn(rem);
          }, 1000);
        }, remaining);
        // Store timeout ref for cleanup
        autoTickRef.current = timeout;
      }
    }
  }, []);

  const stopAutoTick = React.useCallback(() => {
    setAutoTickActive(false);
    setNextTickIn(0);
    localStorage.removeItem('autoTickMinutes');
    localStorage.removeItem('autoTickNextRun');
    if (autoTickRef.current) { clearInterval(autoTickRef.current); clearTimeout(autoTickRef.current); }
    if (countdownRef.current) clearInterval(countdownRef.current);
    autoTickRef.current = null;
    countdownRef.current = null;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoTickRef.current) clearInterval(autoTickRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  // Tier salary config from league_tier_config table
  const [tierConfigs, setTierConfigs] = useState<any[]>([]);
  const [tierSaving, setTierSaving] = useState(false);

  // Salary cap state
  const [selectedLeagueId, setSelectedLeagueId] = useState<string>('');
  const [leagueCapData, setLeagueCapData] = useState<any>(null);
  const [capLoading, setCapLoading] = useState(false);
  const [capError, setCapError] = useState<string | null>(null);

  // New player form
  const [newPlayer, setNewPlayer] = useState({
    firstName: '',
    lastName: '',
    position: 'CF',
    overall: 70,
    age: 25,
    height: 72,
    weight: 180,
    throwingHand: 'R',
    battingHand: 'R',
    power: 60,
    contact: 60,
    speed: 60,
    fielding: 60,
    arm: 60,
    discipline: 60,
    endurance: 60,
    teamId: '',
    ownerEmail: '',
  });

  // Player transfer form
  const [transferPlayer, setTransferPlayer] = useState({ playerId: '', teamId: '' });
  
  // Team league/division transfer
  const [transferTeam, setTransferTeam] = useState({ teamId: '', newLeague: '', newDivision: '' });
  
  // BALLS form
  const [ballsTransfer, setBallsTransfer] = useState({ userId: '', amount: 0, note: '' });

  useEffect(() => {
    loadData();
    loadConfig();
    loadLeagues();
    loadSimulationState();
    loadTierConfigs();
  }, []);

  // Load salary cap data when league is selected
  useEffect(() => {
    if (selectedLeagueId) {
      loadSalaryCapData(selectedLeagueId);
    } else {
      setLeagueCapData(null);
    }
  }, [selectedLeagueId]);

  const loadLeagues = async () => {
    const loadedLeagues = await getAllLeagues();
    const loadedSets = await getAllLeagueSets();
    // Only show traditional (regular) leagues in commissioner sim controls — quick leagues manage themselves
    setLeagues(loadedLeagues.filter(l => l.league_type === LEAGUE_TYPE.TRADITIONAL || !l.league_type));
    setLeagueSets(loadedSets);
  };

  const loadSimLogs = async () => {
    setSimLogLoading(true);
    if (isSupabaseConfigured() && supabase) {
      const { data } = await supabase
        .from('sim_tick_log')
        .select('*')
        .order('started_at', { ascending: false })
        .limit(50);
      setSimLogs(data || []);
    }
    setSimLogLoading(false);
  };

  const loadSimulationState = async () => {
    const state = await getSimulationState();
    setSimState(state);
    const season = getCurrentSeason();
    setCurrentSeason(season);
  };

  const loadTierConfigs = async () => {
    if (isSupabaseConfigured() && supabase) {
      const { data, error } = await supabase
        .from('league_tier_config')
        .select('*')
        .order('tier', { ascending: true });
      if (data && !error) {
        setTierConfigs(data);
      } else {
        console.error('Failed to load tier configs:', error);
      }
    }
  };

  // Load salary cap data for a league
  const loadSalaryCapData = async (leagueId: string) => {
    if (!isSupabaseConfigured() || !supabase) return;
    setCapLoading(true);
    setCapError(null);
    try {
      // Get league info
      const { data: league } = await supabase
        .from('leagues')
        .select('id, name, tier')
        .eq('id', leagueId)
        .single();

      if (!league) {
        setCapError('League not found');
        setCapLoading(false);
        return;
      }

      // Get tier config
      const { data: tierConfig } = await supabase
        .from('league_tier_config')
        .select('salary_cap')
        .eq('tier', league.tier)
        .single();

      const salaryCap = tierConfig?.salary_cap || 0;

      // Get all teams in this league
      const { data: teams } = await supabase
        .from('teams')
        .select('id, name, city, tier')
        .eq('league_id', leagueId);

      if (!teams || teams.length === 0) {
        setLeagueCapData({ salaryCap, totalPayroll: 0, avgPayroll: 0, teams: [] });
        setCapLoading(false);
        return;
      }

      // Get contract counts and total payroll per team
      const teamIds = teams.map(t => t.id);
      const { data: contracts } = await supabase
        .from('player_contracts')
        .select('team_id, salary')
        .in('team_id', teamIds)
        .eq('status', 'active');

      // Build team payroll map
      const payrollMap: Record<string, number> = {};
      const contractCountMap: Record<string, number> = {};
      for (const c of contracts || []) {
        payrollMap[c.team_id] = (payrollMap[c.team_id] || 0) + c.salary;
        contractCountMap[c.team_id] = (contractCountMap[c.team_id] || 0) + 1;
      }

      const teamData = teams.map(t => ({
        id: t.id,
        name: t.name,
        city: t.city,
        tier: t.tier,
        payroll: payrollMap[t.id] || 0,
        contractCount: contractCountMap[t.id] || 0,
        salaryCap,
      }));

      const totalPayroll = teamData.reduce((s: number, t: any) => s + t.payroll, 0);

      setLeagueCapData({
        salaryCap,
        totalPayroll,
        avgPayroll: Math.round(totalPayroll / teamData.length),
        teams: teamData,
      });
    } catch (err: any) {
      setCapError(err.message || 'Failed to load salary cap data');
    }
    setCapLoading(false);
  };

  const loadConfig = async () => {
    if (isSupabaseConfigured() && supabase) {
      const { data } = await supabase.from('game_config').select('*').eq('id', 'forge_config').single();
      if (data?.config) {
        setConfig(data.config);
        return;
      }
    }
    const saved = getItem<ForgeConfig>('forgeConfig');
    if (saved) setConfig(saved);
  };

  const saveConfig = async () => {
    setItem('forgeConfig', config);
    if (isSupabaseConfigured() && supabase) {
      await supabase.from('game_config').upsert({
        id: 'forge_config',
        config: config,
        updated_at: new Date().toISOString()
      });
    }
    setMessage('✅ Config saved!');
    setTimeout(() => setMessage(''), 3000);
  };

  const addCommissioner = async () => {
    if (!newCommissionerEmail || !newCommissionerEmail.includes('@')) {
      setMessage('❌ Invalid email');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    const email = newCommissionerEmail.toLowerCase().trim();
    if (config.commissionerEmails.includes(email)) {
      setMessage('❌ Email already a commissioner');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    const updated = { ...config, commissionerEmails: [...config.commissionerEmails, email] };
    setConfig(updated);
    setNewCommissionerEmail('');
    setMessage('✅ Commissioner added! Click Save Config to persist.');
    setTimeout(() => setMessage(''), 3000);
  };

  const removeCommissioner = (email: string) => {
    const updated = { ...config, commissionerEmails: config.commissionerEmails.filter(e => e !== email) };
    setConfig(updated);
    setMessage('✅ Commissioner removed! Click Save Config to persist.');
    setTimeout(() => setMessage(''), 3000);
  };

  const loadData = async () => {
    // Load users
    if (isSupabaseConfigured() && supabase) {
      const { data: usersData } = await supabase.from('users').select('*');
      if (usersData) setUsers(usersData);
      
      const { data: playersData } = await supabase.from('players').select('*');
      if (playersData) setPlayers(playersData);
      
      const { data: teamsData } = await supabase.from('teams').select('*');
      if (teamsData) setTeams(teamsData);
    } else {
      const localUsers = getItem<StoredUser[]>('users') || [];
      setUsers(localUsers);
      const localPlayers = getItem<StoredPlayer[]>('players') || [];
      setPlayers(localPlayers);
      const localTeams = getItem<StoredTeam[]>('teams') || [];
      setTeams(localTeams);
    }
  };

  const createPlayer = async () => {
    const playerId = `player_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const attributes: Record<string, number> = {
      power: newPlayer.power,
      contact: newPlayer.contact,
      speed: newPlayer.speed,
      fielding: newPlayer.fielding,
      arm: newPlayer.arm,
      discipline: newPlayer.discipline,
      endurance: newPlayer.endurance,
    };

    // Generate random face
    const face = generateRandomFace();

    // Find user by email
    let ownerUserId = newPlayer.teamId || users[0]?.id || 'admin';
    if (newPlayer.ownerEmail) {
      const foundUser = users.find(u => u.email === newPlayer.ownerEmail);
      if (foundUser) {
        ownerUserId = foundUser.id;
      }
    }

    const player = {
      id: playerId,
      first_name: newPlayer.firstName,
      last_name: newPlayer.lastName,
      position: newPlayer.position,
      throwing_hand: newPlayer.throwingHand,
      batting_hand: newPlayer.battingHand,
      height: newPlayer.height,
      weight: newPlayer.weight,
      age: newPlayer.age,
      overall: newPlayer.overall,
      generation: 1,
      forge_seasons_remaining: 10,
      attributes,
      user_id: ownerUserId,
      team_id: newPlayer.teamId || null,
      is_hitter: newPlayer.position !== 'P',
      // Face fields
      bg_color: face.bgColor,
      eye_style: face.eyeStyle,
      eye_color: face.eyeColor,
      eye_size: face.eyeSize,
      mouth_style: face.mouthStyle,
      mouth_color: face.mouthColor,
      hair_style: face.hairStyle,
      hair_color: face.hairColor,
      facial_hair_style: face.facialHairStyle,
      facial_hair_color: face.facialHairColor,
    };

    if (isSupabaseConfigured() && supabase) {
      await supabase.from('players').insert(player);
    } else {
      const localPlayers = getItem<StoredPlayer[]>('players') || [];
      // Convert to StoredPlayer format (camelCase)
      localPlayers.push({
        id: player.id,
        userId: player.user_id,
        firstName: player.first_name,
        lastName: player.last_name,
        position: player.position,
        throwingHand: player.throwing_hand,
        battingHand: player.batting_hand,
        height: player.height,
        weight: player.weight,
        age: player.age,
        overall: player.overall,
        attributes: player.attributes,
        face: {
          bgColor: player.bg_color,
          eyeStyle: player.eye_style,
          eyeColor: player.eye_color,
          eyeSize: player.eye_size,
          mouthStyle: player.mouth_style,
          mouthColor: player.mouth_color,
          hairStyle: player.hair_style,
          hairColor: player.hair_color,
          facialHairStyle: player.facial_hair_style,
          facialHairColor: player.facial_hair_color,
        },
        createdAt: Date.now(),
      });
      setItem('players', localPlayers);
    }

    setMessage(`✅ Created ${newPlayer.firstName} ${newPlayer.lastName}`);
    setTimeout(() => setMessage(''), 3000);
    loadData();
  };

  const transferPlayerToTeam = async () => {
    if (!transferPlayer.playerId || !transferPlayer.teamId) return;

    if (isSupabaseConfigured() && supabase) {
      await supabase.from('players').update({ user_id: transferPlayer.teamId }).eq('id', transferPlayer.playerId);
    } else {
      const localPlayers = getItem<StoredPlayer[]>('players') || [];
      const idx = localPlayers.findIndex(p => p.id === transferPlayer.playerId);
      if (idx >= 0) {
        localPlayers[idx].userId = transferPlayer.teamId;
        setItem('players', localPlayers);
      }
    }

    setMessage('✅ Player transferred!');
    setTimeout(() => setMessage(''), 3000);
    loadData();
  };

  const handleTeamLeagueTransfer = async () => {
    if (!transferTeam.teamId || !transferTeam.newLeague) return;
    
    const newTier = leagues.find(l => l.id === transferTeam.newLeague)?.tier || 1;
    
    // Count existing teams in target league to determine conference/division
    if (isSupabaseConfigured() && supabase) {
      const { data: existingTeams } = await supabase
        .from('teams')
        .select('conference, division')
        .eq('league_id', transferTeam.newLeague);
      
      const count = existingTeams?.length || 0;
      
      let conference: string, division: string;
      if (count < 5) { conference = 'American'; division = 'East'; }
      else if (count < 10) { conference = 'American'; division = 'West'; }
      else if (count < 15) { conference = 'National'; division = 'East'; }
      else { conference = 'National'; division = 'West'; }
      
      // Update team
      await supabase.from('teams').update({ 
        tier: newTier,
        league_id: transferTeam.newLeague,
        division,
        conference,
      }).eq('id', transferTeam.teamId);
      
      // Clear old standings and create fresh
      await supabase.from('league_standings').delete().eq('team_id', transferTeam.teamId);
      
      await supabase.from('league_standings').insert({
        league_id: transferTeam.newLeague,
        team_id: transferTeam.teamId,
        wins: 0,
        losses: 0,
        runs_scored: 0,
        runs_allowed: 0,
        division,
        conference,
      });
    }
    
    setMessage('✅ Team transferred to new league with proper division/conference!');
    setTimeout(() => setMessage(''), 3000);
    loadData();
  };

  const assignBalls = async () => {
    if (!ballsTransfer.userId || ballsTransfer.amount === 0) return;

    try {
      // Call the RPC to atomically update balance
      const { error: rpcError } = await supabase.rpc('increment_balls_balance', {
        target_user_id: ballsTransfer.userId,
        amount: ballsTransfer.amount
      });
      if (rpcError) throw rpcError;

      // Log the transaction
      const { error: txError } = await supabase.from('balls_transactions').insert({
        user_id: ballsTransfer.userId,
        amount: ballsTransfer.amount,
        type: 'commissioner_grant',
        description: ballsTransfer.note || `Commissioner granted ${ballsTransfer.amount.toLocaleString()} BALLS`
      });
      if (txError) console.warn('Transaction log failed:', txError.message);

      setMessage(`✅ Assigned ${ballsTransfer.amount.toLocaleString()} BALLS to user`);
      setTimeout(() => setMessage(''), 3000);
      setBallsTransfer({ userId: '', amount: 0, note: '' });
    } catch (err: any) {
      setMessage(`❌ Failed: ${err.message || 'Unknown error'}`);
      setTimeout(() => setMessage(''), 5000);
    }
  };

  const positions = ['P', 'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'];

  return (
    <div className="commissioner-page">
      <div className="commissioner-header">
        <h1>🎛️ Commissioner Command Center</h1>
        <p>Admin controls for SimForge Baseball</p>
      </div>

      {message && <div className="message-toast">{message}</div>}

      {/* Tabs */}
      <div className="comm-tabs">
        <button className={`comm-tab ${activeTab === 'config' ? 'active' : ''}`} onClick={() => setActiveTab('config')}>⚙️ Game Config</button>
        <button className={`comm-tab ${activeTab === 'players' ? 'active' : ''}`} onClick={() => setActiveTab('players')}>👥 Create Player</button>
        <button className={`comm-tab ${activeTab === 'teams' ? 'active' : ''}`} onClick={() => setActiveTab('teams')}>🔄 Team Transfer</button>
        <button className={`comm-tab ${activeTab === 'balls' ? 'active' : ''}`} onClick={() => setActiveTab('balls')}>💰 BALLS</button>
        <button className={`comm-tab ${activeTab === 'tier-salaries' ? 'active' : ''}`} onClick={() => setActiveTab('tier-salaries')}>💰 League Salaries</button>
        <button className={`comm-tab ${activeTab === 'salary-cap' ? 'active' : ''}`} onClick={() => setActiveTab('salary-cap')}>📊 Salary Cap</button>
        <button className={`comm-tab ${activeTab === 'leagues' ? 'active' : ''}`} onClick={() => setActiveTab('leagues')}>🏆 Leagues</button>
        <button className={`comm-tab ${activeTab === 'simulation' ? 'active' : ''}`} onClick={() => setActiveTab('simulation')}>🎮 Simulation</button>
        <button className={`comm-tab ${activeTab === 'generate' ? 'active' : ''}`} onClick={() => setActiveTab('generate')}>🧪 Generate League</button>
        <button className={`comm-tab ${activeTab === 'team-queue' ? 'active' : ''}`} onClick={() => setActiveTab('team-queue')}>📋 Team Queue</button>
        <button className={`comm-tab ${activeTab === 'commissioners' ? 'active' : ''}`} onClick={() => setActiveTab('commissioners')}>🎛️ Commissioners</button>
        <button className={`comm-tab ${activeTab === 'sim-log' ? 'active' : ''}`} onClick={() => { setActiveTab('sim-log'); loadSimLogs(); }}>📊 Sim Log</button>
        <button className={`comm-tab ${activeTab === 'season-transition' ? 'active' : ''}`} style={{ background: activeTab === 'season-transition' ? 'rgba(234,179,8,0.2)' : undefined, borderColor: activeTab === 'season-transition' ? '#eab308' : undefined }} onClick={() => { setActiveTab('season-transition'); loadLeagues(); }}>🏁 Season Transition</button>
      </div>

      {/* Config Tab */}
      {activeTab === 'config' && (
        <div className="comm-section">
          <h2>Game Configuration</h2>
          
          <div className="config-group">
            <h3>Fees</h3>
            <label>
              Base Breeding Fee:
              <input type="number" value={config.breedingFeeBase} onChange={e => setConfig({...config, breedingFeeBase: Number(e.target.value)})} />
            </label>
            <label>
              Minimum Listing Fee:
              <input type="number" value={config.breedingFeeListingMin} onChange={e => setConfig({...config, breedingFeeListingMin: Number(e.target.value)})} />
            </label>
          </div>

          <div className="config-group">
            <h3>Fee Distribution</h3>
            <label>
              Listing Owner %:
              <input type="number" step="0.01" value={config.feeDistribution.listingOwner} onChange={e => setConfig({...config, feeDistribution: {...config.feeDistribution, listingOwner: Number(e.target.value)}})} />
            </label>
            <label>
              League Pot %:
              <input type="number" step="0.01" value={config.feeDistribution.leaguePot} onChange={e => setConfig({...config, feeDistribution: {...config.feeDistribution, leaguePot: Number(e.target.value)}})} />
            </label>
            <label>
              Dev Fund %:
              <input type="number" step="0.01" value={config.feeDistribution.devFund} onChange={e => setConfig({...config, feeDistribution: {...config.feeDistribution, devFund: Number(e.target.value)}})} />
            </label>
            <small>Total: {(config.feeDistribution.listingOwner + config.feeDistribution.leaguePot + config.feeDistribution.devFund).toFixed(2)}</small>
          </div>

          <div className="config-group">
            <h3>Limits</h3>
            <label>
              Max Breeds Per Player/Season:
              <input type="number" value={config.maxBreedsPerPlayerPerSeason} onChange={e => setConfig({...config, maxBreedsPerPlayerPerSeason: Number(e.target.value)})} />
            </label>
            <label>
              Max Players Received/Season:
              <input type="number" value={config.maxNewPlayersReceivedPerSeason} onChange={e => setConfig({...config, maxNewPlayersReceivedPerSeason: Number(e.target.value)})} />
            </label>
            <label>
              Forge Seasons After Retirement:
              <input type="number" value={config.totalForgeSeasonsAfterRetirement} onChange={e => setConfig({...config, totalForgeSeasonsAfterRetirement: Number(e.target.value)})} />
            </label>
          </div>

          <button className="save-btn" onClick={saveConfig}>💾 Save Config</button>
        </div>
      )}

      {/* Create Player Tab */}
      {activeTab === 'players' && (
        <div className="comm-section">
          <h2>Create Custom Player</h2>
          
          <div className="form-row">
            <label>First Name: <input type="text" value={newPlayer.firstName} onChange={e => setNewPlayer({...newPlayer, firstName: e.target.value})} /></label>
            <label>Last Name: <input type="text" value={newPlayer.lastName} onChange={e => setNewPlayer({...newPlayer, lastName: e.target.value})} /></label>
          </div>
          
          <div className="form-row">
            <label>Position:
              <select value={newPlayer.position} onChange={e => setNewPlayer({...newPlayer, position: e.target.value})}>
                {positions.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>
            <label>Team:
              <select value={newPlayer.teamId} onChange={e => setNewPlayer({...newPlayer, teamId: e.target.value})}>
                <option value="">-- No Team --</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.city} {t.name}</option>)}
              </select>
            </label>
          </div>

          <div className="form-row">
            <label>Owner Email:
              <select value={newPlayer.ownerEmail} onChange={e => setNewPlayer({...newPlayer, ownerEmail: e.target.value})}>
                <option value="">-- Commissioner --</option>
                {users.filter(u => u.email).map(u => <option key={u.id} value={u.email}>{u.email}</option>)}
              </select>
            </label>
          </div>

          <div className="form-row">
            <label>Age: <input type="number" value={newPlayer.age} onChange={e => setNewPlayer({...newPlayer, age: Number(e.target.value)})} /></label>
            <label>Overall: <input type="number" value={newPlayer.overall} onChange={e => setNewPlayer({...newPlayer, overall: Number(e.target.value)})} /></label>
          </div>

          <div className="form-row">
            <label>Height (in): <input type="number" value={newPlayer.height} onChange={e => setNewPlayer({...newPlayer, height: Number(e.target.value)})} /></label>
            <label>Weight (lbs): <input type="number" value={newPlayer.weight} onChange={e => setNewPlayer({...newPlayer, weight: Number(e.target.value)})} /></label>
          </div>

          <div className="form-row">
            <label>Throws: 
              <select value={newPlayer.throwingHand} onChange={e => setNewPlayer({...newPlayer, throwingHand: e.target.value})}>
                <option value="R">R</option>
                <option value="L">L</option>
              </select>
            </label>
            <label>Bats:
              <select value={newPlayer.battingHand} onChange={e => setNewPlayer({...newPlayer, battingHand: e.target.value})}>
                <option value="R">R</option>
                <option value="L">L</option>
                <option value="S">S</option>
              </select>
            </label>
          </div>

          <h3>Attributes</h3>
          <div className="attributes-grid">
            {['power', 'contact', 'speed', 'fielding', 'arm', 'discipline', 'endurance'].map(attr => (
              <label key={attr}>
                {attr.charAt(0).toUpperCase() + attr.slice(1)}:
                <div className="attr-input">
                  <input 
                    type="range" 
                    min="10" 
                    max="99" 
                    value={newPlayer[attr as keyof typeof newPlayer] as number} 
                    onChange={e => setNewPlayer({...newPlayer, [attr]: Number(e.target.value)})} 
                  />
                  <span>{numberToGrade(newPlayer[attr as keyof typeof newPlayer] as number)}</span>
                </div>
              </label>
            ))}
          </div>

          <button className="save-btn" onClick={createPlayer}>➕ Create Player</button>
        </div>
      )}

      {/* Team Transfer Tab */}
      {activeTab === 'teams' && (
        <div className="comm-section">
          <h2>🔄 Transfer Team Between Leagues</h2>
          
          <div className="transfer-team-form">
            <label>Select Team:
              <select value={transferTeam.teamId} onChange={e => setTransferTeam({...transferTeam, teamId: e.target.value, newLeague: '', newDivision: ''})}>
                <option value="">-- Select Team --</option>
                {teams.map(t => (
                  <option key={t.id} value={t.id}>{t.city} {t.name} (Tier {t.tier})</option>
                ))}
              </select>
            </label>

            <label>Transfer to League:
              <select value={transferTeam.newLeague} onChange={e => setTransferTeam({...transferTeam, newLeague: e.target.value})}>
                <option value="">-- Select League --</option>
                {leagues.map(l => (
                  <option key={l.id} value={l.id}>{l.name} (Tier {l.tier})</option>
                ))}
              </select>
            </label>

            <label>Division:
              <select value={transferTeam.newDivision} onChange={e => setTransferTeam({...transferTeam, newDivision: e.target.value})}>
                <option value="">-- Auto --</option>
                <option value="East">East</option>
                <option value="West">West</option>
                <option value="Central">Central</option>
                <option value="North">North</option>
                <option value="South">South</option>
              </select>
            </label>

            <button className="save-btn" onClick={handleTeamLeagueTransfer}>⬆️ Transfer Team</button>
          </div>

          {/* AI Team Generator */}
          <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid rgba(255,255,255,0.08)' }} />
          <h2>🤖 Generate AI Teams</h2>
          <p className="section-desc">Create AI-owned teams for a league</p>
          <div className="generate-ai-form">
            <label>Target League:
              <select id="ai-target-league">
                <option value="">-- Select League --</option>
                {leagues.map(l => <option key={l.id} value={l.id}>{l.name} (Tier {l.tier})</option>)}
              </select>
            </label>
            <label>Number of Teams:
              <input type="number" id="ai-team-count" defaultValue={6} min={1} max={20} className="small-input" />
            </label>
            <button className="save-btn" onClick={async () => {
              const leagueId = (document.getElementById('ai-target-league') as HTMLSelectElement)?.value;
              const count = parseInt((document.getElementById('ai-team-count') as HTMLInputElement)?.value || '6');
              if (!leagueId) { alert('Select a league first'); return; }
              if (!isSupabaseConfigured() || !supabase) return;

              const league = leagues.find(l => l.id === leagueId);
              setMessage(`⏳ Generating ${count} AI teams...`);

              const cityNames = ['Springfield', 'Riverside', 'Lakewood', 'Fairview', 'Georgetown', 'Madison', 'Franklin', 'Greenville', 'Clinton', 'Salem', 'Burlington', 'Manchester', 'Oxford', 'Arlington', 'Ashland', 'Winchester', 'Dayton', 'Lexington', 'Marion', 'Lebanon'];
              const teamNames = ['Titans', 'Eagles', 'Wolves', 'Hawks', 'Panthers', 'Lions', 'Bears', 'Dragons', 'Knights', 'Warriors', 'Raptors', 'Storm', 'Thunder', 'Blaze', 'Cobras', 'Falcons', 'Vipers', 'Sharks', 'Scorpions', 'Cyclones'];
              const abbrs = ['SPR', 'RIV', 'LKW', 'FAV', 'GEO', 'MAD', 'FRN', 'GRV', 'CLI', 'SAL', 'BUR', 'MAN', 'OXF', 'ARL', 'ASH', 'WIN', 'DAY', 'LEX', 'MAR', 'LEB'];

              for (let i = 0; i < count; i++) {
                const city = cityNames[i % cityNames.length];
                const name = teamNames[i % teamNames.length];
                const abbr = abbrs[i % abbrs.length];

                const { data: newTeam } = await supabase.from('teams').insert({
                  city, name, abbreviation: abbr,
                  tier: league?.tier || 5,
                  league_id: leagueId,
                  is_ai: true,
                  conference: i < count / 2 ? 'American' : 'National',
                  division: i % 2 === 0 ? 'East' : 'West',
                  wins: 0, losses: 0, runs_scored: 0, runs_allowed: 0,
                }).select().single();

                // Generate 25 players for this team
                if (newTeam) {
                  const positions = ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH', 'C', '1B', '2B', 'SS', 'LF', 'RF', 'P', 'P', 'P', 'P', 'P', 'P', 'P', 'P', 'P', 'P'];
                  const firstNames = [
                    // Classic American
                    'James', 'John', 'Robert', 'Michael', 'William', 'David', 'Richard', 'Joseph', 'Thomas', 'Charles',
                    'Christopher', 'Daniel', 'Matthew', 'Anthony', 'Mark', 'Donald', 'Steven', 'Paul', 'Andrew', 'Joshua',
                    'Kenneth', 'Kevin', 'Brian', 'George', 'Timothy', 'Ronald', 'Jason', 'Edward', 'Jeffrey', 'Ryan',
                    // Modern American
                    'Tyler', 'Dylan', 'Cody', 'Trevor', 'Blake', 'Cole', 'Hunter', 'Mason', 'Logan', 'Ethan',
                    'Jacob', 'Noah', 'Liam', 'Aiden', 'Caden', 'Carter', 'Owen', 'Wyatt', 'Grayson', 'Landon',
                    // Latin American (MLB is ~30% Latino)
                    'Carlos', 'Jose', 'Juan', 'Luis', 'Miguel', 'Pedro', 'Rafael', 'Roberto', 'Ramon', 'Ricardo',
                    'Alejandro', 'Eduardo', 'Fernando', 'Ernesto', 'Francisco', 'Diego', 'Javier', 'Manuel', 'Angel', 'Yoel',
                    'Yadier', 'Yasmani', 'Yoenis', 'Yulieski', 'Ronald', 'Yordan', 'Eloy', 'Edwin', 'Nelson', 'Jorge',
                    // Diverse
                    'Jordan', 'Xavier', 'Marcus', 'DeAndre', 'Malik', 'Isaiah', 'Elijah', 'Damon', 'Derrick', 'Tyrell',
                    'Shohei', 'Masahiro', 'Hideki', 'Ichiro', 'Yu', 'Kenta', 'Koji',
                    'Hyun-Jin', 'Ji-Man', 'Shin-Soo', 'Byung-Ho',
                    'Aaron', 'Adam', 'Austin', 'Ben', 'Chad', 'Connor', 'Derek', 'Evan', 'Frank', 'Gary',
                    'Gavin', 'Gerald', 'Henry', 'Jack', 'Jake', 'Jared', 'Jesse', 'Josh', 'Justin', 'Lance',
                    'Lee', 'Leo', 'Leonard', 'Marcus', 'Max', 'Miles', 'Nathan', 'Nick', 'Oliver', 'Oscar',
                    'Pablo', 'Patrick', 'Peter', 'Philip', 'Quinn', 'Randy', 'Reggie', 'Rex', 'Rocky', 'Roman',
                    'Sam', 'Scott', 'Sean', 'Shawn', 'Spencer', 'Stan', 'Terry', 'Todd', 'Tony', 'Travis',
                    'Troy', 'Victor', 'Walter', 'Wayne', 'Wesley', 'Zach', 'Zion'
                  ];
                  const lastNames = [
                    // Common American
                    'Smith', 'Johnson', 'Williams', 'Jones', 'Brown', 'Davis', 'Miller', 'Wilson', 'Moore', 'Taylor',
                    'Anderson', 'Thomas', 'Jackson', 'White', 'Harris', 'Martin', 'Thompson', 'Young', 'Allen', 'King',
                    'Wright', 'Scott', 'Torres', 'Nguyen', 'Hill', 'Flores', 'Green', 'Adams', 'Nelson', 'Baker',
                    'Hall', 'Rivera', 'Campbell', 'Mitchell', 'Carter', 'Roberts', 'Gomez', 'Phillips', 'Evans', 'Turner',
                    'Parker', 'Collins', 'Edwards', 'Stewart', 'Morris', 'Murphy', 'Cook', 'Rogers', 'Morgan', 'Peterson',
                    'Cooper', 'Reed', 'Bailey', 'Bell', 'Howard', 'Ward', 'Cox', 'Richardson', 'Wood', 'Watson',
                    'Brooks', 'Bennett', 'Gray', 'Price', 'Long', 'Foster', 'Sanders', 'Powell', 'Jenkins', 'Perry',
                    'Russell', 'Butler', 'Barnes', 'Fisher', 'Henderson', 'Simmons', 'Bryant', 'Griffin', 'Hayes', 'Myers',
                    'Ford', 'Hamilton', 'Graham', 'Sullivan', 'Wallace', 'Palmer', 'Ross', 'Marshall', 'Ellis', 'Wells',
                    // Latin American
                    'Garcia', 'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez', 'Perez', 'Sanchez', 'Ramirez', 'Cruz',
                    'Ortiz', 'Gutierrez', 'Chavez', 'Ramos', 'Gomez', 'Vasquez', 'Castillo', 'Jimenez', 'Moreno', 'Romero',
                    'Herrera', 'Medina', 'Aguilar', 'Vargas', 'Fernandez', 'Guerrero', 'Diaz', 'Alvarez', 'Mendoza', 'Ruiz',
                    'Salazar', 'Soto', 'Acosta', 'Delgado', 'Pena', 'Rios', 'Reyes', 'Morales', 'Ortega', 'Silva',
                    'Contreras', 'Mejia', 'Cabrera', 'Velazquez', 'Figueroa', 'Espinoza', 'Estrada', 'Dominguez', 'Santiago',
                    // Asian
                    'Kim', 'Lee', 'Park', 'Choi', 'Lim', 'Sato', 'Suzuki', 'Tanaka', 'Yamamoto', 'Matsui',
                    'Nakamura', 'Yoshida', 'Kobayashi', 'Ishikawa', 'Ohtani', 'Darvish',
                    // Other diverse
                    'Kowalski', 'Petrov', 'Novak', 'Schmidt', 'Weber', 'Fischer', 'Wagner', 'Becker', 'Hoffmann',
                    "O'Brien", "O'Connor", 'Murphy', 'Kelly', 'Ryan', 'Flynn', 'Walsh', 'Kennedy', 'Fitzgerald',
                    'DuBois', 'Laurent', 'Beaumont',
                    'Judge', 'Trout', 'Betts', 'Soto', 'Harper', 'Machado', 'Arenado', 'Goldschmidt', 'Bregman', 'Altuve'
                  ];

                  for (let j = 0; j < 25; j++) {
                    const pos = positions[j];
                    const isP = pos === 'P';
                    const rs = (min: number, max: number) => Math.floor(min + Math.random() * (max - min + 1));
                    const fn = firstNames[rs(0, firstNames.length - 1)];
                    const ln = lastNames[rs(0, lastNames.length - 1)] + (j > 14 ? String(rs(1, 9)) : '');

                    const attrs = isP
                      ? { velocity: rs(40, 70), control: rs(35, 65), movement: rs(35, 65), stamina: rs(40, 70), power: 30, contact: 30, speed: 40, fielding: 45, arm: 50, discipline: 40, endurance: 50 }
                      : { power: rs(30, 65), contact: rs(30, 65), speed: rs(30, 65), fielding: rs(30, 65), arm: rs(30, 65), discipline: rs(30, 65), endurance: rs(30, 65), velocity: 30, control: 30, movement: 30, stamina: 30 };

                    const ovr = isP ? Math.round((attrs.velocity + attrs.control + attrs.movement + attrs.stamina) / 4) : Math.round((attrs.power + attrs.contact + attrs.speed + attrs.fielding + attrs.arm + attrs.discipline + attrs.endurance) / 7);

                    await supabase.from('players').insert({
                      first_name: fn, last_name: ln, position: pos,
                      throwing_hand: 'R', batting_hand: 'R',
                      height: 70 + rs(0, 7), weight: 170 + rs(0, 50), age: 20 + rs(0, 8),
                      overall: ovr, is_hitter: !isP, current_stamina: 100,
                      team_id: newTeam.id, ...attrs,
                      clutch: rs(30, 70), consistency: rs(30, 70), durability: rs(30, 70),
                      work_ethic: rs(30, 70), leadership: rs(30, 70), intelligence: rs(30, 70),
                    });
                  }
                }
              }

              setMessage(`✅ Generated ${count} AI teams with ${count * 25} players!`);
              loadData();
              loadLeagues();
              setTimeout(() => setMessage(''), 5000);
            }}>
              🤖 Generate AI Teams
            </button>
          </div>

          <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid rgba(255,255,255,0.08)' }} />

          <h2>👥 Transfer Player to Team</h2>
          
          <label>Select Player:
            <select value={transferPlayer.playerId} onChange={e => setTransferPlayer({...transferPlayer, playerId: e.target.value})}>
              <option value="">-- Select Player --</option>
              {players.map(p => (
                <option key={p.id} value={p.id}>{p.first_name} {p.last_name} ({p.position})</option>
              ))}
            </select>
          </label>

          <label>Transfer to Team:
            <select value={transferPlayer.teamId} onChange={e => setTransferPlayer({...transferPlayer, teamId: e.target.value})}>
              <option value="">-- Select Team --</option>
              {teams.map(t => (
                <option key={t.id} value={t.id}>{t.city} {t.name}</option>
              ))}
            </select>
          </label>

          <button className="save-btn" onClick={transferPlayerToTeam}>🔄 Transfer Player</button>

          <h3>All Players ({players.length})</h3>
          <div className="data-table">
            <table>
              <thead><tr><th>Name</th><th>Pos</th><th>Team</th><th>OVR</th></tr></thead>
              <tbody>
                {players.slice(0, 20).map(p => (
                  <tr key={p.id}>
                    <td>{p.first_name} {p.last_name}</td>
                    <td>{p.position}</td>
                    <td>{p.user_id?.slice(0, 8) || 'Free'}</td>
                    <td>{numberToGrade(p.overall)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* BALLS Tab */}
      {activeTab === 'balls' && (
        <div className="comm-section">
          <h2>Assign BALLS to Account</h2>
          
          <label>Select User:
            <select value={ballsTransfer.userId} onChange={e => setBallsTransfer({...ballsTransfer, userId: e.target.value})}>
              <option value="">-- Select User --</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.username} ({u.email})</option>
              ))}
            </select>
          </label>

          <label>Amount (BALLS):
            <input 
              type="number" 
              value={ballsTransfer.amount} 
              onChange={e => setBallsTransfer({...ballsTransfer, amount: Number(e.target.value)})}
              placeholder="10000"
            />
          </label>

          <label>Note:
            <input 
              type="text" 
              value={ballsTransfer.note} 
              onChange={e => setBallsTransfer({...ballsTransfer, note: e.target.value})}
              placeholder="Testing bonus, etc."
            />
          </label>

          <button className="save-btn" onClick={assignBalls}>💰 Assign BALLS</button>

          <h3>All Users ({users.length})</h3>
          <div className="data-table">
            <table>
              <thead><tr><th>Username</th><th>Email</th><th>ID</th></tr></thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td>{u.username}</td>
                    <td>{u.email}</td>
                    <td className="mono">{u.id?.slice(0, 12)}...</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Commissioners Tab */}
      {activeTab === 'commissioners' && (
        <div className="comm-section">
          <h2>Manage Commissioners</h2>
          <p className="section-desc">Only commissioners can access the Commissioner panel</p>

          <div className="add-commissioner-form">
            <label>Add Commissioner by Email:
              <input 
                type="email" 
                value={newCommissionerEmail}
                onChange={e => setNewCommissionerEmail(e.target.value)}
                placeholder="email@example.com"
              />
            </label>
            <button className="save-btn" onClick={addCommissioner}>➕ Add Commissioner</button>
          </div>

          <h3>Current Commissioners ({config.commissionerEmails.length})</h3>
          <div className="commissioners-list">
            {config.commissionerEmails.map(email => (
              <div key={email} className="commissioner-item">
                <span className="commissioner-email">{email}</span>
                <button 
                  className="remove-btn"
                  onClick={() => removeCommissioner(email)}
                >
                  ❌ Remove
                </button>
              </div>
            ))}
          </div>

          <p className="help-text">
            💡 Tip: Click "Save Config" after adding/removing commissioners to persist changes.
          </p>
        </div>
      )}

      {/* Leagues Tab */}
      {activeTab === 'leagues' && (
        <div className="comm-section">
          <h2>🏆 League Management</h2>
          <p className="section-desc">Create and manage leagues with different tiers</p>

          {/* Create New League */}
          <div className="create-league-form">
            <h3>Create New League</h3>
            <div className="form-row">
              <label>
                League Name:
                <input 
                  type="text" 
                  value={newLeagueName}
                  onChange={e => setNewLeagueName(e.target.value)}
                  placeholder="e.g., American League"
                />
              </label>
              <label>
                Tier:
                <select 
                  value={newLeagueTier} 
                  onChange={e => setNewLeagueTier(Number(e.target.value) as 1|2|3|4|5)}
                >
                  <option value={1}>Elite</option>
                  <option value={2}>Platinum</option>
                  <option value={3}>Gold</option>
                  <option value={4}>Silver</option>
                  <option value={5}>Bronze</option>
                </select>
              </label>
              <label className="checkbox-label">
                <input 
                  type="checkbox"
                  checked={newLeagueAiRun}
                  onChange={e => setNewLeagueAiRun(e.target.checked)}
                />
                🤖 AI-Run (Developmental)
              </label>
              <label>
                League Type:
                <select 
                  value={newLeagueType} 
                  onChange={e => setNewLeagueType(e.target.value)}
                >
                  <option value="traditional">Traditional (Permanent)</option>
                  <option value="quick">Quick League</option>
                </select>
              </label>
              <button 
                className="save-btn"
                onClick={async () => {
                  if (!newLeagueName.trim()) {
                    setMessage('❌ Please enter a league name');
                    setTimeout(() => setMessage(''), 3000);
                    return;
                  }
                  if (!isSupabaseConfigured() || !supabase) {
                    setMessage('❌ Supabase not connected');
                    setTimeout(() => setMessage(''), 3000);
                    return;
                  }

                  setMessage('🔨 Creating ' + newLeagueName + '... (this takes ~30s)');

                  try {
                    // 1. Create league
                    const leagueId = 'league_' + Date.now() + '_' + Math.random().toString(36).substr(2, 6);
                    await supabase.from('leagues').insert({
                      id: leagueId, name: newLeagueName, tier: newLeagueTier,
                      status: 'active', sim_speed: 5, entry_fee: 10000,
                      is_ai_run: newLeagueAiRun,
                      league_type: newLeagueType,
                      current_season: 1,
                      created_at: new Date().toISOString(),
                      updated_at: new Date().toISOString(),
                    });

                    // 2. Create 24 teams
                    const cities = ['Austin','Miami','Denver','Seattle','Phoenix','Atlanta','Dallas','Boston',
                      'Charlotte','Nashville','Orlando','Raleigh','Portland','Salt Lake','Albuquerque','Tucson',
                      'Memphis','Louisville','Oklahoma City','El Paso','San Antonio','Tampa','Kansas City','Las Vegas'];
                    const names = ['Bombers','Rays','Rockies','Storm','Diamondbacks','Braves','Rangers','Sox',
                      'Hornets','Stars','Magic','Hurricanes','Trail','Blaze','Pistols','Scorpions',
                      'Grizzlies','Fire','Thunder','Rattlers','Spurs','Buccaneers','Chiefs','Knights'];
                    const abbrs = cities.map(c => c.slice(0, 3).toUpperCase());

                    const teamIds: string[] = [];
                    for (let t = 0; t < 24; t++) {
                      const teamId = 't_' + leagueId.slice(-6) + '_' + t;
                      const conf = t < 12 ? 'American' : 'National';
                      const div = (t % 12) < 6 ? 'East' : 'West';
                      await supabase.from('teams').insert({
                        id: teamId, city: cities[t], name: names[t], abbreviation: abbrs[t],
                        tier: newLeagueTier, league_id: leagueId, is_ai: true,
                        conference: conf, division: div,
                        wins: 0, losses: 0, runs_scored: 0, runs_allowed: 0,
                        rating: 50,
                      });
                      teamIds.push(teamId);
                    }

                    // 3. Create 25 players per team
                    const positions = ['C','1B','2B','3B','SS','LF','CF','RF','DH',
                      'P','P','P','P','P','P','P','P','P','P','P','P','P','P','P','P'];
                    const fn = ['Jake','Sam','Luke','Tom','Jose','Mike','Ryan','Alex','Chris','Dan'];
                    const ln = ['Smith','Johnson','Garcia','Miller','Wilson','Lee','Brown','Davis','Clark','Hall'];
                    const rs = (min: number, max: number) => min + Math.floor(Math.random() * (max - min + 1));

                    setMessage('🔨 ' + newLeagueName + ': Creating 600 players...');

                    for (let t = 0; t < 24; t++) {
                      for (let p = 0; p < 25; p++) {
                        const pos = positions[p];
                        const isP = pos === 'P';
                        await supabase.from('players').insert({
                          id: 'p_' + leagueId.slice(-6) + '_' + t + '_' + p,
                          first_name: fn[rs(0, 9)],
                          last_name: ln[rs(0, 9)] + (p > 14 ? rs(1, 9) : ''),
                          position: pos, throwing_hand: 'R', batting_hand: Math.random() > 0.85 ? 'L' : 'R',
                          height: 70 + rs(0, 10), weight: 170 + rs(0, 50), age: 22 + rs(0, 15),
                          is_hitter: !isP, retired: false, current_stamina: 100, team_id: teamIds[t],
                          league_id: leagueId,
                          power: isP ? 30 : rs(35, 70), contact: isP ? 30 : rs(35, 70),
                          speed: isP ? 40 : rs(35, 70), fielding: isP ? 30 : rs(35, 70),
                          range_attr: rs(35, 70), glove_attr: isP ? 30 : rs(35, 70),
                          arm: isP ? 50 : rs(35, 70), discipline: isP ? 40 : rs(35, 70),
                          endurance: rs(35, 70), velocity: isP ? rs(45, 70) : 30,
                          movement: isP ? rs(35, 70) : 30, control: isP ? rs(40, 65) : 30,
                          deception: isP ? rs(35, 70) : 30, clutch: rs(35, 70),
                          consistency: rs(35, 70), durability: rs(35, 70), intelligence: rs(35, 70),
                          batting_attr: isP ? 30 : rs(35, 70),
                        });
                      }
                    }

                    // 4. Create standings
                    const standings = teamIds.map((tid, i) => ({
                      league_id: leagueId, team_id: tid,
                      wins: 0, losses: 0, runs_scored: 0, runs_allowed: 0,
                      division: (i % 12) < 6 ? 'East' : 'West',
                      conference: i < 12 ? 'American' : 'National',
                    }));
                    await supabase.from('league_standings').insert(standings);

                    // 5. Generate schedule
                    setMessage('🔨 ' + newLeagueName +': Generating schedule...');

                    const divGroups: Record<string, string[]> = {};
                    for (let i = 0; i < 24; i++) {
                      const conf = i < 12 ? 'American' : 'National';
                      const div = (i % 12) < 6 ? 'East' : 'West';
                      const key = conf + '-' + div;
                      if (!divGroups[key]) divGroups[key] = [];
                      divGroups[key].push(teamIds[i]);
                    }

                    const matchups: [string, string, number][] = [];
                    const seen = new Set<string>();
                    const addPair = (a: string, b: string, n: number) => {
                      const k = [a, b].sort().join('|');
                      if (seen.has(k)) return;
                      seen.add(k);
                      matchups.push([a, b, n]);
                    };

                    // Intradiv
                    for (const [, dt] of Object.entries(divGroups)) {
                      for (let i = 0; i < dt.length; i++)
                        for (let j = i + 1; j < dt.length; j++)
                          addPair(dt[i], dt[j], 13);
                    }
                    // Cross-div conference
                    const ac = [...(divGroups['American-East'] || []), ...(divGroups['American-West'] || [])];
                    const nc = [...(divGroups['National-East'] || [])];
                    // Cross-division
                    for (const e of divGroups['American-East'] || [])
                      for (const w of divGroups['American-West'] || []) addPair(e, w, 6);
                    for (const e of divGroups['National-East'] || [])
                      for (const w of divGroups['National-West'] || []) addPair(e, w, 6);
                    // Interleague
                    const allAL = [...(divGroups['American-East'] || []), ...(divGroups['American-West'] || [])];
                    const allNL = [...(divGroups['National-East'] || []), ...(divGroups['National-West'] || [])];
                    for (const a of allAL) for (const b of allNL) addPair(a, b, 4);

                    const games: any[] = [];
                    const busy: Record<string, Set<number>> = {};
                    for (const tid of teamIds) busy[tid] = new Set();
                    let gid = 1;

                    matchups.sort((a, b) => b[2] - a[2]);
                    for (const [a, b, n] of matchups) {
                      let last = 0, home = true;
                      for (let g = 0; g < n; g++) {
                        let d = last + Math.max(2, Math.floor(160 / n));
                        while (d < 300) {
                          if (!busy[a].has(d) && !busy[b].has(d)) {
                            busy[a].add(d); busy[b].add(d);
                            games.push({
                              league_id: leagueId, game_day: d,
                              home_team_id: home ? a : b, away_team_id: home ? b : a,
                              status: 'scheduled', game_number: 1,
                              game_id: 'g' + leagueId.slice(-6) + '_' + gid++
                            });
                            last = d; home = !home; break;
                          }
                          d++;
                        }
                      }
                    }

                    // Batch insert
                    const BATCH = 100;
                    for (let i = 0; i < games.length; i += BATCH) {
                      await supabase.from('league_schedule').insert(games.slice(i, i + BATCH));
                    }

                    setMessage(`✅ ${newLeagueName} created! 24 teams, 600 players, ${games.length} games scheduled`);
                    setNewLeagueName('');
                    loadLeagues();
                  } catch (err: any) {
                    setMessage('❌ Failed: ' + (err.message || err));
                  }
                  setTimeout(() => setMessage(''), 8000);
                }}
                disabled={createLeagueLoading}
              >
                {createLeagueLoading ? '⏳ Creating...' : '➕ Create League'}
              </button>
            </div>
          </div>

          {/* League Sets */}
          <div className="league-sets-section">
            <h3>League Sets (Season Schedules)</h3>
            <p className="help-text">
              League sets define when seasons start/end. All leagues in a set run on the same schedule.
            </p>
            {leagueSets.length === 0 ? (
              <p className="empty-state">No league sets created yet. Default set is used.</p>
            ) : (
              <div className="league-sets-list">
                {leagueSets.map(set => (
                  <div key={set.id} className="league-set-card">
                    <strong>{set.name}</strong>
                    {set.description && <p>{set.description}</p>}
                    <span className={`status-badge ${set.is_active ? 'active' : ''}`}>
                      {set.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Existing Leagues */}
          <div className="existing-leagues">
            <h3>Existing Leagues ({leagues.length})</h3>
            {leagues.length === 0 ? (
              <p className="empty-state">No leagues created yet</p>
            ) : (
              <div className="leagues-grid">
                {[1,2,3,4,5].map(tier => {
                  const tierLeagues = leagues.filter(l => l.tier === tier);
                  if (tierLeagues.length === 0) return null;
                  return (
                    <div key={tier} className="tier-group">
                      <h4 style={{ color: TIER_COLORS[tier] }}>
                        {TIER_NAMES[tier]} ({tierLeagues.length})
                      </h4>
                      {tierLeagues.map(league => (
                        <div key={league.id} className="league-item">
                          <div className="league-info">
                            <span className="league-name">{league.name}</span>
                            {league.is_ai_run && (
                              <span className="ai-badge">🤖 AI</span>
                            )}
                            <span className="league-fee">
                              Fee: {(league.entry_fee || 10000).toLocaleString()} $BALLS
                            </span>
                          </div>
                          <div className="league-actions">
                            <select 
                              value={league.status}
                              onChange={async (e) => {
                                await updateLeagueStatus(league.id, e.target.value as any);
                                loadLeagues();
                              }}
                              className="status-select"
                            >
                              <option value="upcoming">Upcoming</option>
                              <option value="active">Active</option>
                              <option value="paused">Paused</option>
                              <option value="completed">Completed</option>
                            </select>
                            {league.status === 'completed' && (
                              <button
                                className="distribute-btn"
                                onClick={async () => {
                                  if (!confirm(`Distribute prizes for ${league.name}?`)) return;
                                  const { distributePrizes } = await import('../services/PrizeService');
                                  const result = await distributePrizes(league.id);
                                  if (result.success) {
                                    const ord = (n: number) => { const s = ['th','st','nd','rd']; const v = n % 100; return n + (s[(v-20)%10] || s[v] || s[0]); };
                                    const summary = result.payouts.map(p => `${p.team}: ${p.amount.toLocaleString()} $BALLS (${ord(p.position)})`).join('\n');
                                    alert(`✅ ${result.message}\n\n${summary}`);
                                  } else {
                                    alert(`❌ ${result.message}`);
                                  }
                                }}
                                title="Distribute Prizes"
                              >
                                💰 Distribute
                              </button>
                            )}
                            <button 
                              className="delete-btn"
                              onClick={async () => {
                                if (!confirm(`Delete "${league.name}" and all associated data? This cannot be undone.`)) return;
                                if (isSupabaseConfigured() && supabase) {
                                  // Unassign teams from this league
                                  await supabase.from('teams').update({ 
                                    league_id: null, 
                                    conference: null, 
                                    division: null 
                                  }).eq('league_id', league.id);
                                  // Delete standings
                                  await supabase.from('league_standings').delete().eq('league_id', league.id);
                                  // Delete schedule
                                  await supabase.from('league_schedule').delete().eq('league_id', league.id);
                                  // Delete playoff brackets
                                  await supabase.from('playoff_brackets').delete().eq('league_id', league.id);
                                  await supabase.from('playoff_series').delete().eq('league_id', league.id);
                                  await supabase.from('playoff_games').delete().eq('league_id', league.id);
                                  // Delete league
                                  await supabase.from('leagues').delete().eq('id', league.id);
                                }
                                loadLeagues();
                                loadData();
                                setMessage(`🗑️ Deleted ${league.name} — teams unassigned`);
                                setTimeout(() => setMessage(''), 3000);
                              }}
                              title="Delete League"
                            >
                              🗑️
                            </button>
                          </div>
                          {/* Economics editor */}
                          <LeagueEconomics 
                            league={league} 
                            onSaved={() => { loadLeagues(); setMessage('✅ Saved!'); setTimeout(() => setMessage(''), 2000); }} 
                          />
                          {/* Tier controls */}
                          <div className="tier-controls">
                            <span className="tier-label">Tier: {TIER_NAMES[league.tier]}</span>
                            {league.tier > 1 && (
                              <button className="tier-btn promote" onClick={async () => {
                                if (!confirm(`Promote ${league.name} to ${TIER_NAMES[league.tier - 1]}?`)) return;
                                await supabase!.from('leagues').update({ tier: league.tier - 1 }).eq('id', league.id);
                                // Also update all teams in this league
                                await supabase!.from('teams').update({ tier: league.tier - 1 }).eq('league_id', league.id);
                                loadLeagues();
                                setMessage(`⬆️ Promoted ${league.name} to ${TIER_NAMES[league.tier - 1]}`);
                                setTimeout(() => setMessage(''), 3000);
                              }}>⬆️ Promote</button>
                            )}
                            {league.tier < 5 && (
                              <button className="tier-btn demote" onClick={async () => {
                                if (!confirm(`Relegate ${league.name} to ${TIER_NAMES[league.tier + 1]}?`)) return;
                                await supabase!.from('leagues').update({ tier: league.tier + 1 }).eq('id', league.id);
                                await supabase!.from('teams').update({ tier: league.tier + 1 }).eq('league_id', league.id);
                                loadLeagues();
                                setMessage(`⬇️ Relegated ${league.name} to ${TIER_NAMES[league.tier + 1]}`);
                                setTimeout(() => setMessage(''), 3000);
                              }}>⬇️ Relegate</button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Custom League Creator */}
          <div className="custom-league-section" style={{marginTop: 30, padding: 20, background: 'rgba(34,197,94,0.1)', borderRadius: 12, border: '1px solid rgba(34,197,94,0.3)'}}>
            <h3 style={{color: '#22c55e'}}>🎯 Create Custom Featured League</h3>
            <p className="section-desc">Create leagues with custom entry fees that appear on the home page</p>
            
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginTop: 16}}>
              <label style={{display: 'flex', flexDirection: 'column', gap: 4}}>
                <span style={{color: '#888', fontSize: 12}}>League Name</span>
                <input id="customLeagueName" type="text" defaultValue="Pro League" 
                  style={{padding: 8, borderRadius: 6, border: '1px solid #30363d', background: '#0d1117', color: '#fff'}} />
              </label>
              
              <label style={{display: 'flex', flexDirection: 'column', gap: 4}}>
                <span style={{color: '#888', fontSize: 12}}>League Type ID</span>
                <input id="customLeagueType" type="text" defaultValue="pro_12"
                  style={{padding: 8, borderRadius: 6, border: '1px solid #30363d', background: '#0d1117', color: '#fff'}} />
              </label>
              
              <label style={{display: 'flex', flexDirection: 'column', gap: 4}}>
                <span style={{color: '#888', fontSize: 12}}>Max Teams</span>
                <input id="customMaxTeams" type="number" defaultValue={12}
                  style={{padding: 8, borderRadius: 6, border: '1px solid #30363d', background: '#0d1117', color: '#fff'}} />
              </label>
              
              <label style={{display: 'flex', flexDirection: 'column', gap: 4}}>
                <span style={{color: '#888', fontSize: 12}}>Entry Fee ($BALLS)</span>
                <input id="customEntryFee" type="number" defaultValue={500}
                  style={{padding: 8, borderRadius: 6, border: '1px solid #30363d', background: '#0d1117', color: '#fff'}} />
              </label>
              
              <label style={{display: 'flex', flexDirection: 'column', gap: 4}}>
                <span style={{color: '#888', fontSize: 12}}>Prize Pool (0 = auto)</span>
                <input id="customPrizePool" type="number" defaultValue={0}
                  style={{padding: 8, borderRadius: 6, border: '1px solid #30363d', background: '#0d1117', color: '#fff'}} />
              </label>
              
              <label style={{display: 'flex', flexDirection: 'column', gap: 4}}>
                <span style={{color: '#888', fontSize: 12}}>Sim Speed (1-5)</span>
                <select id="customSimSpeed" defaultValue="1"
                  style={{padding: 8, borderRadius: 6, border: '1px solid #30363d', background: '#0d1117', color: '#fff'}}>
                  <option value="1">1 - Instant (1 min)</option>
                  <option value="2">2 - Turbo (2 min)</option>
                  <option value="3">3 - Fast (10 min)</option>
                  <option value="4">4 - Normal (30 min)</option>
                  <option value="5">5 - Slow (120 min)</option>
                </select>
              </label>
              
              <label style={{display: 'flex', flexDirection: 'column', gap: 4}}>
                <span style={{color: '#888', fontSize: 12}}>Payout Structure</span>
                <select id="customPayoutType" onChange={(e) => {
                  const custom = document.getElementById('customPayoutCustom');
                  if (custom) (custom as HTMLDivElement).style.display = e.target.value === 'custom' ? 'block' : 'none';
                }}
                  style={{padding: 8, borderRadius: 6, border: '1px solid #30363d', background: '#0d1117', color: '#fff'}}>
                  <option value="standard">Standard (50/30/10/10)</option>
                  <option value="winner_takes_all">Winner Takes All (100%)</option>
                  <option value="top_heavy">Top Heavy (70/20/5/5)</option>
                  <option value="even_split">Even Split (25% each)</option>
                  <option value="custom">Custom...</option>
                </select>
              </label>
            </div>

            {/* Custom Payout Config */}
            <div id="customPayoutCustom" style={{display: 'none', marginTop: 12, padding: 12, background: 'rgba(0,0,0,0.3)', borderRadius: 8}}>
              <div style={{color: '#888', fontSize: 12, marginBottom: 8}}>Custom Payout Percentages (must sum to 100)</div>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8}}>
                <label style={{display: 'flex', flexDirection: 'column', gap: 4}}>
                  <span style={{color: '#666', fontSize: 11}}>Champion %</span>
                  <input id="customPayoutChamp" type="number" defaultValue={50} min={0} max={100}
                    style={{padding: 6, borderRadius: 4, border: '1px solid #30363d', background: '#0d1117', color: '#fff'}} />
                </label>
                <label style={{display: 'flex', flexDirection: 'column', gap: 4}}>
                  <span style={{color: '#666', fontSize: 11}}>Runner-Up %</span>
                  <input id="customPayoutRunner" type="number" defaultValue={30} min={0} max={100}
                    style={{padding: 6, borderRadius: 4, border: '1px solid #30363d', background: '#0d1117', color: '#fff'}} />
                </label>
                <label style={{display: 'flex', flexDirection: 'column', gap: 4}}>
                  <span style={{color: '#666', fontSize: 11}}>SF Losers % (each)</span>
                  <input id="customPayoutSF" type="number" defaultValue={10} min={0} max={100}
                    style={{padding: 6, borderRadius: 4, border: '1px solid #30363d', background: '#0d1117', color: '#fff'}} />
                </label>
              </div>
            </div>

            {/* Presets */}
            <div style={{marginTop: 16}}>
              <span style={{color: '#888', fontSize: 12, marginRight: 8}}>Quick Presets:</span>
              {[
                { label: 'Practice', name: 'Practice League', type: 'practice_12', max: 12, fee: 50, speed: 1 },
                { label: 'Standard', name: 'Quick League', type: 'standard_12', max: 12, fee: 100, speed: 1 },
                { label: 'Pro', name: 'Pro League', type: 'pro_12', max: 12, fee: 500, speed: 2 },
                { label: 'Whale', name: 'Whale League', type: 'whale_12', max: 12, fee: 2000, speed: 3 },
                { label: 'Championship', name: 'Championship', type: 'championship_24', max: 24, fee: 250, speed: 2 },
                { label: 'Dynasty', name: 'Dynasty Series', type: 'dynasty_24', max: 24, fee: 1000, speed: 4 },
              ].map(p => (
                <button key={p.type} onClick={() => {
                  (document.getElementById('customLeagueName') as HTMLInputElement).value = p.name;
                  (document.getElementById('customLeagueType') as HTMLInputElement).value = p.type;
                  (document.getElementById('customMaxTeams') as HTMLInputElement).value = String(p.max);
                  (document.getElementById('customEntryFee') as HTMLInputElement).value = String(p.fee);
                  (document.getElementById('customSimSpeed') as HTMLSelectElement).value = String(p.speed);
                }}
                style={{
                  padding: '6px 12px', marginRight: 6, marginTop: 6, borderRadius: 4,
                  border: '1px solid #30363d', background: '#21262d', color: '#fff', cursor: 'pointer', fontSize: 12
                }}>
                  {p.label}
                </button>
              ))}
            </div>

            <button onClick={async () => {
              const name = (document.getElementById('customLeagueName') as HTMLInputElement)?.value;
              const league_type = (document.getElementById('customLeagueType') as HTMLInputElement)?.value;
              const max_teams = parseInt((document.getElementById('customMaxTeams') as HTMLInputElement)?.value || '12');
              const entry_fee = parseInt((document.getElementById('customEntryFee') as HTMLInputElement)?.value || '100');
              const prize_pool = parseInt((document.getElementById('customPrizePool') as HTMLInputElement)?.value || '0');
              const sim_speed = parseInt((document.getElementById('customSimSpeed') as HTMLSelectElement)?.value || '1');
              const payout_type = (document.getElementById('customPayoutType') as HTMLSelectElement)?.value || 'standard';
              
              // Get payout config
              let payout_config: any = { semifinal_losers: 10, runner_up: 30, champion: 50 };
              if (payout_type === 'winner_takes_all') {
                payout_config = { semifinal_losers: 0, runner_up: 0, champion: 100 };
              } else if (payout_type === 'top_heavy') {
                payout_config = { semifinal_losers: 5, runner_up: 20, champion: 70 };
              } else if (payout_type === 'even_split') {
                payout_config = { semifinal_losers: 25, runner_up: 25, champion: 25 };
              } else if (payout_type === 'custom') {
                const champ = parseInt((document.getElementById('customPayoutChamp') as HTMLInputElement)?.value || '50');
                const runner = parseInt((document.getElementById('customPayoutRunner') as HTMLInputElement)?.value || '30');
                const sf = parseInt((document.getElementById('customPayoutSF') as HTMLInputElement)?.value || '10');
                const total = champ + runner + (sf * 2);
                if (total !== 100) {
                  setMessage(`❌ Payout must sum to 100% (currently ${total}%)`);
                  setTimeout(() => setMessage(''), 3000);
                  return;
                }
                payout_config = { semifinal_losers: sf, runner_up: runner, champion: champ };
              }
              
              if (!name || !league_type) {
                setMessage('❌ Name and type required');
                setTimeout(() => setMessage(''), 3000);
                return;
              }
              
              if (!isSupabaseConfigured() || !supabase) {
                setMessage('❌ Supabase not connected');
                setTimeout(() => setMessage(''), 3000);
                return;
              }
              
              const prize = prize_pool || (entry_fee * max_teams);
              const leagueId = 'league_' + Date.now() + '_' + Math.random().toString(36).substr(2, 6);
              
              const { error } = await supabase.from('leagues').insert({
                id: leagueId,
                name, league_type, max_teams, entry_fee, prize_pool: prize, sim_speed, payout_config,
                status: 'active', current_day: 1, tier: 3,
                created_at: new Date().toISOString()
              });
              
              if (error) {
                setMessage('❌ ' + error.message);
              } else {
                setMessage(`✅ ${name} created! ${max_teams} teams × ${entry_fee} = ${prize} $BALLS prize pool`);
                loadLeagues();
              }
              setTimeout(() => setMessage(''), 5000);
            }}
            style={{marginTop: 16, padding: '12px 24px', borderRadius: 8, border: 'none', background: '#22c55e', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer'}}>
              🎯 Create Featured League
            </button>
          </div>

          {message && <div className="message">{message}</div>}
        </div>
      )}

      {/* Tier Salaries Tab */}
      {activeTab === 'tier-salaries' && (
        <div className="comm-section">
          <h2>💰 Traditional League Salary Configuration</h2>
          <p className="section-desc">Set buy-in amounts, salary caps, and contract limits for each tier</p>

          <div className="contracts-grid">
            {tierConfigs.map((tc) => {
              const tier = tc.tier as number;
              return (
                <div key={tier} className="contract-tier-card">
                  <h3 style={{ color: TIER_COLORS[tier] }}>{TIER_NAMES[tier]} Tier</h3>
                  
                  <div className="contract-field">
                    <label>Buy-In Amount (BALLS):</label>
                    <input 
                      type="number" 
                      value={tc.buy_in ?? 0}
                      onChange={(e) => setTierConfigs(tierConfigs.map(c => c.tier === tier ? { ...c, buy_in: parseInt(e.target.value) || 0 } : c))}
                    />
                  </div>
                  
                  <div className="contract-field">
                    <label>Team Salary Cap (BALLS):</label>
                    <input 
                      type="number" 
                      value={tc.salary_cap ?? 0}
                      onChange={(e) => setTierConfigs(tierConfigs.map(c => c.tier === tier ? { ...c, salary_cap: parseInt(e.target.value) || 0 } : c))}
                    />
                  </div>
                  
                  <div className="contract-field">
                    <label>Team Salary Floor (BALLS):</label>
                    <input 
                      type="number" 
                      value={tc.salary_floor ?? 0}
                      onChange={(e) => setTierConfigs(tierConfigs.map(c => c.tier === tier ? { ...c, salary_floor: parseInt(e.target.value) || 0 } : c))}
                    />
                  </div>
                  
                  <div className="contract-field">
                    <label>Player Min Salary (BALLS):</label>
                    <input 
                      type="number" 
                      value={tc.min_salary ?? 0}
                      onChange={(e) => setTierConfigs(tierConfigs.map(c => c.tier === tier ? { ...c, min_salary: parseInt(e.target.value) || 0 } : c))}
                    />
                  </div>
                  
                  <div className="contract-field">
                    <label>Player Max Salary (BALLS):</label>
                    <input 
                      type="number" 
                      value={tc.max_salary ?? 0}
                      onChange={(e) => setTierConfigs(tierConfigs.map(c => c.tier === tier ? { ...c, max_salary: parseInt(e.target.value) || 0 } : c))}
                    />
                  </div>
                  
                  <div className="contract-field">
                    <label>AI Scrub Salary (BALLS):</label>
                    <input 
                      type="number" 
                      value={tc.scrub_salary ?? 0}
                      onChange={(e) => setTierConfigs(tierConfigs.map(c => c.tier === tier ? { ...c, scrub_salary: parseInt(e.target.value) || 0 } : c))}
                    />
                  </div>
                  
                  <div className="contract-field">
                    <label>Max Contract Years:</label>
                    <input 
                      type="number" 
                      value={tc.max_contract_years ?? 3}
                      onChange={(e) => setTierConfigs(tierConfigs.map(c => c.tier === tier ? { ...c, max_contract_years: parseInt(e.target.value) || 1 } : c))}
                      min={1}
                      max={10}
                    />
                  </div>
                  
                  <div className="contract-field">
                    <label>Min Contract Years:</label>
                    <input 
                      type="number" 
                      value={tc.min_contract_years ?? 1}
                      onChange={(e) => setTierConfigs(tierConfigs.map(c => c.tier === tier ? { ...c, min_contract_years: parseInt(e.target.value) || 1 } : c))}
                      min={1}
                      max={5}
                    />
                  </div>
                  
                  <div className="contract-field">
                    <label>Max Signing Bonus (BALLS):</label>
                    <input 
                      type="number" 
                      value={tc.max_signing_bonus ?? 0}
                      onChange={(e) => setTierConfigs(tierConfigs.map(c => c.tier === tier ? { ...c, max_signing_bonus: parseInt(e.target.value) || 0 } : c))}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <button 
            className="save-btn"
            disabled={tierSaving}
            onClick={async () => {
              setTierSaving(true);
              try {
                if (isSupabaseConfigured() && supabase) {
                  const rows = tierConfigs.map(tc => ({
                    tier: tc.tier,
                    name: tc.name,
                    buy_in: tc.buy_in,
                    salary_cap: tc.salary_cap,
                    salary_floor: tc.salary_floor,
                    min_salary: tc.min_salary,
                    max_salary: tc.max_salary,
                    scrub_salary: tc.scrub_salary,
                    max_contract_years: tc.max_contract_years,
                    min_contract_years: tc.min_contract_years,
                    max_signing_bonus: tc.max_signing_bonus,
                    updated_at: new Date().toISOString(),
                  }));
                  const { error } = await supabase
                    .from('league_tier_config')
                    .upsert(rows, { onConflict: 'tier' });
                  if (error) {
                    setMessage(`❌ Save failed: ${error.message}`);
                  } else {
                    setMessage('✅ Tier salary configs saved successfully!');
                    await loadTierConfigs();
                  }
                } else {
                  setMessage('❌ Supabase not configured');
                }
              } catch (err: any) {
                setMessage(`❌ Save failed: ${err.message}`);
              }
              setTierSaving(false);
              setTimeout(() => setMessage(''), 4000);
            }}
          >
            {tierSaving ? '⏳ Saving...' : '💾 Save All Changes'}
          </button>

          {message && <div className="message">{message}</div>}
        </div>
      )}

      {/* Salary Cap Overview Tab */}
      {activeTab === 'salary-cap' && (
        <div className="comm-section">
          <h2>📊 Salary Cap Overview</h2>
          <p className="section-desc">Team-by-team payroll against salary caps across all leagues</p>

          <div className="league-selector" style={{ marginBottom: 20 }}>
            <select 
              value={selectedLeagueId} 
              onChange={(e) => setSelectedLeagueId(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #333', background: '#0d1117', color: '#e8b82a', width: 300 }}
            >
              <option value="">-- Select a league --</option>
              {leagues.map((l: any) => (
                <option key={l.id} value={l.id}>{l.name} (Tier {l.tier})</option>
              ))}
            </select>
          </div>

          {capLoading && <p>Loading salary cap data...</p>}
          {capError && <p className="error-message">{capError}</p>}

          {leagueCapData && (
            <div>
              <div className="cap-summary" style={{ display: 'flex', gap: 20, marginBottom: 20 }}>
                <div className="cap-card" style={{ background: '#0d1117', border: '1px solid #333', borderRadius: 8, padding: 16, flex: 1 }}>
                  <div style={{ color: '#888', fontSize: 12 }}>SALARY CAP</div>
                  <div style={{ color: '#e8b82a', fontSize: 24, fontWeight: 'bold' }}>{leagueCapData.salaryCap.toLocaleString()} BALLS</div>
                </div>
                <div className="cap-card" style={{ background: '#0d1117', border: '1px solid #333', borderRadius: 8, padding: 16, flex: 1 }}>
                  <div style={{ color: '#888', fontSize: 12 }}>LEAGUE TOTAL PAYROLL</div>
                  <div style={{ color: '#fff', fontSize: 24, fontWeight: 'bold' }}>{leagueCapData.totalPayroll.toLocaleString()} BALLS</div>
                </div>
                <div className="cap-card" style={{ background: '#0d1117', border: '1px solid #333', borderRadius: 8, padding: 16, flex: 1 }}>
                  <div style={{ color: '#888', fontSize: 12 }}>AVG TEAM PAYROLL</div>
                  <div style={{ color: '#e8b82a', fontSize: 24, fontWeight: 'bold' }}>{leagueCapData.avgPayroll.toLocaleString()} BALLS</div>
                </div>
              </div>

              <table className="comm-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th>Team</th>
                    <th>Tier</th>
                    <th># Contracts</th>
                    <th>Payroll</th>
                    <th>Cap</th>
                    <th>Cap Space</th>
                    <th>Usage</th>
                  </tr>
                </thead>
                <tbody>
                  {leagueCapData.teams.map((team: any) => {
                    const usagePct = team.salaryCap > 0 ? Math.round((team.payroll / team.salaryCap) * 100) : 0;
                    const isOverCap = team.payroll > team.salaryCap;
                    return (
                      <tr key={team.id} style={{ borderBottom: '1px solid #222' }}>
                        <td style={{ padding: 8, color: '#e8b82a' }}>{team.city} {team.name}</td>
                        <td style={{ padding: 8 }}>Tier {team.tier}</td>
                        <td style={{ padding: 8 }}>{team.contractCount}</td>
                        <td style={{ padding: 8, color: '#fff' }}>{team.payroll.toLocaleString()}</td>
                        <td style={{ padding: 8, color: '#888' }}>{team.salaryCap.toLocaleString()}</td>
                        <td style={{ padding: 8, color: isOverCap ? '#ff4444' : '#4ade80' }}>
                          {isOverCap ? `-${(team.payroll - team.salaryCap).toLocaleString()}` : (team.salaryCap - team.payroll).toLocaleString()}
                        </td>
                        <td style={{ padding: 8 }}>
                          <div style={{ width: 100, height: 8, background: '#222', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{ 
                              width: `${Math.min(usagePct, 100)}%`, 
                              height: '100%', 
                              background: isOverCap ? '#ff4444' : usagePct > 90 ? '#ffa500' : '#4ade80',
                              borderRadius: 4,
                              transition: 'width 0.3s'
                            }} />
                          </div>
                          <span style={{ fontSize: 11, color: isOverCap ? '#ff4444' : '#888' }}>{usagePct}%</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {leagueCapData.teams.length === 0 && <p className="empty-state">No teams found in this league.</p>}
            </div>
          )}

          {!selectedLeagueId && !capLoading && (
            <p className="empty-state">Select a league above to view salary cap data.</p>
          )}
        </div>
      )}

      {/* Simulation Tab */}
      {activeTab === 'simulation' && (
        <div className="comm-section">
          <h2>🎮 Game Simulation Control</h2>
          <p className="section-desc">Start, stop, and control the game simulation</p>

          {/* Current Season Status */}
          <div className="season-status-card">
            <h3>Current Season</h3>
            {currentSeason ? (
              <div className="season-info">
                <div className="season-stat">
                  <span className="stat-label">Year:</span>
                  <span className="stat-value">{currentSeason.year}</span>
                </div>
                <div className="season-stat">
                  <span className="stat-label">Status:</span>
                  <span className={`stat-value status-${currentSeason.status}`}>
                    {currentSeason.status}
                  </span>
                </div>
                <div className="season-stat">
                  <span className="stat-label">Day:</span>
                  <span className="stat-value">{currentSeason.currentDay} / {currentSeason.totalDays}</span>
                </div>
              </div>
            ) : (
              <p className="empty-state">No active season. Start a new season to begin.</p>
            )}
          </div>

          {/* Season Management */}
          <div className="season-status-card" style={{ borderColor: '#f59e0b', borderWidth: '2px' }}>
            <h3>🔄 Season Management</h3>
            
            {leagues.length === 0 ? (
              <p style={{ color: '#888', fontStyle: 'italic' }}>No leagues found.</p>
            ) : (
              <div>
                {/* Begin All Pre-Season Button */}
                {leagues.some(l => l.status === 'pre_season') && (
                  <button
                    className="sim-btn start"
                    style={{ padding: '10px 24px', marginBottom: '12px', width: '100%', fontSize: '14px', fontWeight: 'bold', background: 'linear-gradient(135deg, #38bdf8, #0ea5e9)' }}
                    onClick={async () => {
                      const preLeagues = leagues.filter(l => l.status === 'pre_season');
                      if (!confirm(`Begin season for all ${preLeagues.length} pre-season leagues? (Fills scrubs, enforces 14/11 rosters)`)) return;
                      setMessage(`⏳ Beginning ${preLeagues.length} leagues...`);
                      let ok = 0, fail = 0;
                      const token = (await supabase.auth.getSession()).data.session?.access_token;
                      for (const league of preLeagues) {
                        try {
                          // sim-tick with pre_season status handles 14/11 enforcement + activation in one call
                          const res = await fetch(`${FUNCTIONS_URL}/sim-tick`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${token}`,
                              'x-admin-secret': 'simforge-admin-2024',
                            },
                            body: JSON.stringify({ leagueId: league.id }),
                          });
                          const data = await res.json();
                          if (data.status === 'success' || data.status === 'waiting') { ok++; } else { fail++; }
                        } catch { fail++; }
                      }
                      // Sync global simulation_state
                      if (ok > 0 && supabase) {
                        const yr = preLeagues[0]?.season_year ?? 1901;
                        await supabase.from('simulation_state').update({
                          season_status: 'active', current_day: 1,
                          season_year: yr, sim_date: `${yr}-04-01`,
                          is_running: true, last_tick_at: new Date().toISOString(),
                        }).eq('id', 'global');
                      }
                      loadSimulationState();
                      loadLeagues();
                      setMessage(`🌱 Began ${ok} leagues!${fail ? ` ❌ ${fail} failed — try again.` : ''} Global sim synced.`);
                      setTimeout(() => setMessage(''), 8000);
                    }}
                  >
                    🌱 Begin All Pre-Season Leagues ({leagues.filter(l => l.status === 'pre_season').length})
                  </button>
                )}

                {/* Flip All Button — shows for off-season leagues */}
                {leagues.some(l => l.status === 'off_season') && (
                  <button
                    className="sim-btn start"
                    style={{ padding: '10px 24px', marginBottom: '12px', width: '100%', fontSize: '14px', fontWeight: 'bold' }}
                    onClick={async () => {
                      const offSeasonLeagues = leagues.filter(l => l.status === 'off_season');
                      if (!confirm(`Flip all ${offSeasonLeagues.length} off-season leagues to a new season?`)) return;
                      setMessage(`⏳ Flipping ${offSeasonLeagues.length} leagues...`);
                      let ok = 0, fail = 0;
                      const newYear = Math.max(...offSeasonLeagues.map(l => l.season_year ?? 1900)) + 1;
                      const token = (await supabase.auth.getSession()).data.session?.access_token;
                      for (const league of offSeasonLeagues) {
                        try {
                          const res = await fetch(`${FUNCTIONS_URL}/start-next-season`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${token}`,
                              'x-admin-secret': 'simforge-admin-2024',
                            },
                            body: JSON.stringify({ leagueId: league.id }),
                          });
                          const data = await res.json();
                          if (data.error) { fail++; } else { ok++; }
                        } catch { fail++; }
                      }
                      // Sync global simulation_state so the VPS scheduler knows we're live
                      if (ok > 0 && supabase) {
                        await supabase.from('simulation_state').update({
                          season_status: 'active',
                          current_day: 1,
                          season_year: newYear,
                          sim_date: `${newYear}-04-01`,
                          is_running: true,
                          last_tick_at: new Date().toISOString(),
                        }).eq('id', 'global');
                      }
                      loadSimulationState();
                      loadLeagues();
                      setMessage(`✅ Flipped ${ok} leagues!${fail ? ` ❌ ${fail} failed.` : ''} Global sim now active.`);
                      setTimeout(() => setMessage(''), 8000);
                    }}
                  >
                    ⏭️ Flip All Off-Season Leagues ({leagues.filter(l => l.status === 'off_season').length})
                  </button>
                )}
                {/* Force Flip All — for active leagues, commissioner override */}
                {leagues.some(l => l.status !== 'off_season') && (
                  <button
                    className="sim-btn"
                    style={{ padding: '10px 24px', marginBottom: '16px', width: '100%', fontSize: '14px', fontWeight: 'bold', background: 'rgba(239,68,68,0.15)', borderColor: '#ef4444', color: '#ef4444' }}
                    onClick={async () => {
                      const allLeagues = leagues;
                      if (!confirm(`⚠️ FORCE FLIP ALL ${allLeagues.length} LEAGUES to a new season?\n\nThis will wipe schedules, standings, and game data — even for active leagues. This is a commissioner override. Are you sure?`)) return;
                      setMessage(`⏳ Force-flipping all ${allLeagues.length} leagues...`);
                      let ok = 0, fail = 0;
                      const token = (await supabase.auth.getSession()).data.session?.access_token;
                      for (const league of allLeagues) {
                        try {
                          const res = await fetch(`${FUNCTIONS_URL}/start-next-season`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${token}`,
                              'x-admin-secret': 'simforge-admin-2024',
                            },
                            body: JSON.stringify({ leagueId: league.id, force: true }),
                          });
                          const data = await res.json();
                          if (data.error) { console.error(league.name, data.error); fail++; } else { ok++; }
                        } catch (e: any) { console.error(league.name, e); fail++; }
                      }
                      loadSimulationState();
                      loadLeagues();
                      setMessage(`✅ Force-flipped ${ok} leagues!${fail ? ` ❌ ${fail} failed — check console.` : ''}`);
                      setTimeout(() => setMessage(''), 10000);
                    }}
                  >
                    🔴 Force Flip ALL Leagues ({leagues.length})
                  </button>
                )}

                {/* Start Playoffs for All Leagues — shown when any traditional league is at Day 183+ */}
                {leagues.some(l => l.status === 'playoffs' && !l.champion_team_id && ((l as any).league_type !== 'fast_12' && (l as any).league_type !== 'fast_24')) && (
                  <button
                    className="sim-btn start"
                    style={{ padding: '10px 24px', marginBottom: '12px', width: '100%', fontSize: '14px', fontWeight: 'bold', background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}
                    onClick={async () => {
                      const playoffLeagues = leagues.filter(l =>
                        l.status === 'playoffs' &&
                        !l.champion_team_id &&
                        (l as any).league_type !== 'fast_12' &&
                        (l as any).league_type !== 'fast_24'
                      );
                      if (!confirm(`Start playoffs for ${playoffLeagues.length} league(s)?\n\n${playoffLeagues.map(l => `• ${l.name} (Season ${l.season_year})`).join('\n')}\n\nThis will generate Division Series brackets and schedule all playoff games.`)) return;
                      setMessage(`⏳ Starting playoffs for ${playoffLeagues.length} league(s)...`);
                      let ok = 0, fail = 0, skipped = 0;
                      const results: string[] = [];
                      for (const league of playoffLeagues) {
                        try {
                          const res = await fetch(`${FUNCTIONS_URL}/start-playoffs`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'x-admin-secret': 'simforge-admin-2024',
                            },
                            body: JSON.stringify({ league_id: league.id }),
                          });
                          const data = await res.json();
                          if (data.error) {
                            if (data.error.includes('already exists') || data.error.includes('already in schedule')) {
                              skipped++;
                              results.push(`⏭️ ${league.name}: already started`);
                            } else {
                              fail++;
                              results.push(`❌ ${league.name}: ${data.error}`);
                            }
                          } else {
                            ok++;
                            const matchupStr = (data.matchups || []).map((m: any) => `  ${m.division}: ${m.home} vs ${m.away}`).join('\n');
                            results.push(`✅ ${league.name}: ${data.games_scheduled} games\n${matchupStr}`);
                          }
                        } catch (e: any) {
                          fail++;
                          results.push(`❌ ${league.name}: ${e.message}`);
                        }
                      }
                      loadLeagues();
                      setMessage([
                        `🏆 Playoffs: ${ok} started${skipped ? `, ${skipped} already running` : ''}${fail ? `, ${fail} failed` : ''}`,
                        ...results
                      ].join('\n'));
                      setTimeout(() => setMessage(''), 15000);
                    }}
                  >
                    🏆 Start Playoffs for All Leagues ({leagues.filter(l => l.status === 'playoffs' && !l.champion_team_id && (l as any).league_type !== 'fast_12' && (l as any).league_type !== 'fast_24').length})
                  </button>
                )}

                {leagues.map(league => {
                  const isOffSeason = league.status === 'off_season';
                  const isActive = league.status === 'active';
                  const isPreSeason = league.status === 'pre_season';
                  let statusColor: string = '#888';
                  let statusLabel: string = league.status || 'Unknown';
                  if (isOffSeason) { statusColor = '#f59e0b'; statusLabel = 'Off-Season'; }
                  else if (isActive) { statusColor = '#22c55e'; statusLabel = 'Active'; }
                  else if (isPreSeason) { statusColor = '#38bdf8'; statusLabel = '🌱 Pre-Season'; }
                  return (
                    <div key={league.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', marginBottom: '8px' }}>
                      <div>
                        <strong>{league.name}</strong> — <span style={{ color: statusColor }}>{statusLabel}</span> (Year {league.season_year || '?'}, Day {league.current_day || '?'})
                      </div>
                      {isOffSeason ? (
                        <button
                          className="sim-btn start"
                          style={{ padding: '8px 16px' }}
                          onClick={async () => {
                            setMessage(`⏳ Starting new season for ${league.name}...`);
                            try {
                              const res = await fetch(`${FUNCTIONS_URL}/start-next-season`, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
                                  'x-admin-secret': 'simforge-admin-2024',
                                },
                                body: JSON.stringify({ leagueId: league.id }),
                              });
                              const data = await res.json();
                              if (data.error) {
                                setMessage(`❌ ${data.error}`);
                              } else {
                                // Sync global simulation_state
                                if (supabase) {
                                  await supabase.from('simulation_state').update({
                                    season_status: 'active', current_day: 1,
                                    season_year: data.newYear || (league.season_year ?? 1900) + 1,
                                    sim_date: `${data.newYear || (league.season_year ?? 1900) + 1}-04-01`,
                                    is_running: true,
                                  }).eq('id', 'global');
                                }
                                loadSimulationState();
                                loadLeagues();
                                let msg = `✅ ${league.name} flipped to ${data.newYear}! ${data.gamesGenerated} games ready.`;
                                if (data.playoffsWarning) msg += ` ${data.playoffsWarning}`;
                                setMessage(msg);
                              }
                            } catch (err: any) {
                              setMessage(`❌ Failed: ${err.message}`);
                            }
                            setTimeout(() => setMessage(''), 6000);
                          }}
                        >
                          ⏭️ Start New Season
                        </button>
                      ) : isPreSeason ? (
                        <button
                          className="sim-btn start"
                          style={{ padding: '8px 16px' }}
                          title="Begin the season — enforces 14/11 rosters and starts sim-tick"
                          onClick={async () => {
                            setMessage(`⏳ Beginning season for ${league.name}...`);
                            try {
                              // Trigger sim-tick which will call handlePreSeasonFlip
                              const res = await fetch(`${FUNCTIONS_URL}/sim-tick`, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
                                  'x-admin-secret': 'simforge-admin-2024',
                                },
                                body: JSON.stringify({ leagueId: league.id }),
                              });
                              const data = await res.json();
                              loadSimulationState();
                              loadLeagues();
                              if (data?.status === 'active' || data?.status === 'waiting') {
                                setMessage(`✅ ${league.name} season begun! Pre-Season → Active`);
                              } else {
                                setMessage(`✅ ${league.name} sim-tick triggered: ${data?.status || 'ok'}`);
                              }
                            } catch (err: any) {
                              // The sim-tick might have already flipped it
                              loadSimulationState();
                              loadLeagues();
                              setMessage(`✅ ${league.name} — check status, may have begun`);
                            }
                            setTimeout(() => setMessage(''), 5000);
                          }}
                        >
                          🌱 Begin Season
                        </button>
                      ) : (
                        <button
                          className="sim-btn"
                          style={{ padding: '8px 16px', background: 'rgba(239,68,68,0.12)', borderColor: '#ef4444', color: '#ef4444', fontSize: '12px' }}
                          title="Force flip this active league to a new season (commissioner override)"
                          onClick={async () => {
                            if (!confirm(`Force flip ${league.name} to new season? This wipes current schedule and standings.`)) return;
                            setMessage(`⏳ Force-flipping ${league.name}...`);
                            try {
                              const res = await fetch(`${FUNCTIONS_URL}/start-next-season`, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
                                  'x-admin-secret': 'simforge-admin-2024',
                                },
                                body: JSON.stringify({ leagueId: league.id, force: true }),
                              });
                              const data = await res.json();
                              if (data.error) {
                                setMessage(`❌ ${data.error}`);
                              } else {
                                loadSimulationState();
                                loadLeagues();
                                setMessage(`✅ ${league.name} force-flipped to ${data.newYear}!`);
                              }
                            } catch (err: any) {
                              setMessage(`❌ Failed: ${err.message}`);
                            }
                            setTimeout(() => setMessage(''), 6000);
                          }}
                        >
                          🔴 Force Flip
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Simulation Controls */}
          <div className="sim-controls-card">
            <h3>Simulation Engine</h3>
            
            <div className="sim-status">
              <span className="sim-indicator">
                {simState.is_running ? '🔴 Running' : '⚪ Stopped'}
              </span>
              <span className="sim-day">Day {simState.current_day}</span>
            </div>

            <div className="sim-speed-control" style={{ margin: '10px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label>⏱️ Speed:</label>
              <select 
                value={simState.sim_speed || 'normal'}
                onChange={async (e) => {
                  const speed = e.target.value;
                  if (isSupabaseConfigured() && supabase) {
                    await supabase.from('simulation_state').update({ sim_speed: speed }).eq('id', 'global');
                    setSimState({ ...simState, sim_speed: speed });
                    setMessage(`⏱️ Sim speed set to ${speed}`);
                    setTimeout(() => setMessage(''), 3000);
                  }
                }}
                style={{ padding: '5px 10px', borderRadius: '4px', background: '#1a1f2e', color: '#fff', border: '1px solid #333' }}
              >
                <option value="slow">🐢 Slow (1 day / 60 min)</option>
                <option value="normal">🚶 Normal (1 day / 30 min)</option>
                <option value="fast">🏃 Fast (1 day / 10 min)</option>
                <option value="turbo">🚀 Turbo (1 day / 2 min)</option>
              </select>
            </div>

            <div className="skip-offseason-control" style={{ margin: '10px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input 
                  type="checkbox" 
                  checked={simState.skip_off_season || false}
                  onChange={async (e) => {
                    const skip = e.target.checked;
                    if (isSupabaseConfigured() && supabase) {
                      await supabase.from('simulation_state').update({ skip_off_season: skip }).eq('id', 'global');
                      setSimState({ ...simState, skip_off_season: skip });
                      setMessage(skip ? '⚡ Skip Off-Season enabled' : '📅 Normal off-season');
                      setTimeout(() => setMessage(''), 3000);
                    }
                  }}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span>⚡ Skip Off-Season (testing mode)</span>
              </label>
            </div>

            <div className="sim-buttons">
              {simState.is_running ? (
                <button 
                  className="sim-btn stop"
                  onClick={async () => {
                    await stopSimulation();
                    loadSimulationState();
                    setMessage('⏹️ Simulation stopped');
                    setTimeout(() => setMessage(''), 3000);
                  }}
                >
                  ⏹️ Stop Simulation
                </button>
              ) : (
                <button 
                  className="sim-btn start"
                  onClick={async () => {
                    await startSimulation();
                    loadSimulationState();
                    setMessage('▶️ Simulation started');
                    setTimeout(() => setMessage(''), 3000);
                  }}
                >
                  ▶️ Start Simulation
                </button>
              )}

              <button 
                className="sim-btn advance"
                onClick={async () => {
                  const supabaseKey = (await supabase.auth.getSession()).data.session?.access_token || "";
                  if (!FUNCTIONS_URL) { setMessage('❌ No Supabase URL configured'); return; }
                  const activeLeagues = leagues.filter(l => l.status === 'active');
                  if (!activeLeagues.length) { setMessage('❌ No active leagues found'); return; }
                  setMessage('⚾ Advancing 1 day via server engine...');
                  try {
                    let total = 0;
                    const results: string[] = [];
                    for (const league of activeLeagues) {
                      const res = await fetch(`${FUNCTIONS_URL}/sim-tick`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${supabaseKey}`,
                        },
                        body: JSON.stringify({ leagueId: league.id }),
                      });
                      const data = await res.json();
                      total += data.games || 0;
                      results.push(`${league.name}: ${data.games || 0} games`);
                      console.log(`Advance tick ${league.name}:`, data);
                    }
                    loadSimulationState();
                    setMessage(`✅ ${total} games simulated (${results.join(', ')})`);
                  } catch (error) {
                    console.error('Advance day error:', error);
                    setMessage(`❌ Error: ${error}`);
                  }
                  setTimeout(() => setMessage(''), 6000);
                }}
              >
                ⏭️ Advance 1 Day
              </button>

              <button
                className="sim-btn edge"
                title="Manually fire the edge function (same as what the Cloudflare Worker does)"
                onClick={async () => {
                  const supabaseKey = (await supabase.auth.getSession()).data.session?.access_token || "";
                  if (!FUNCTIONS_URL) { setMessage('❌ No Supabase URL configured'); return; }
                  setMessage('🔁 Triggering edge function...');
                  try {
                    const activeLeagues = leagues.filter(l => l.status === 'active');
                    if (!activeLeagues.length) { setMessage('❌ No active leagues found'); return; }
                    let total = 0;
                    for (const league of activeLeagues) {
                      const res = await fetch(`${FUNCTIONS_URL}/sim-tick`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${supabaseKey}`,
                        },
                        body: JSON.stringify({ leagueId: league.id }),
                      });
                      const data = await res.json();
                      total += data.games || 0;
                      console.log(`Edge tick ${league.name}:`, data);
                    }
                    loadSimulationState();
                    setMessage(`✅ Edge function fired: ${total} games simulated across ${activeLeagues.length} leagues`);
                  } catch (err) {
                    setMessage(`❌ Edge function error: ${err}`);
                  }
                  setTimeout(() => setMessage(''), 6000);
                }}
              >
                🔁 Trigger Edge Tick
              </button>

              <button 
                className="sim-btn turbo"
                onClick={async () => {
                  setMessage('⚡ Simulating 20 days...');
                  try {
                    let totalGames = 0;
                    for (let i = 0; i < 20; i++) {
                      const currentDay = await getCurrentDay();
                      if (currentDay === 0) {
                        await advanceToDayOne();
                        loadSimulationState();
                        continue;
                      }
                      const simResult = await simulateDay(currentDay);
                      if (simResult.gamesPlayed === 0) break;
                      totalGames += simResult.gamesPlayed;
                      
                      // Check season complete
                      const leagues = await getAllLeagues();
                      for (const league of leagues) {
                        const complete = await isRegularSeasonComplete(league.id);
                        if (complete && league.status === 'active') {
                          await startPlayoffs(league.id);
                          await updateLeagueStatus(league.id, 'completed');
                          const { archiveSeason } = await import('../services/SeasonHistoryService');
                          await archiveSeason(league.id, new Date().getFullYear());
                        }
                      }
                      
                      const newDay = currentDay + 1;
                      if (newDay === 7) {
                        await checkAndFillRosters();
                      }
                      await advanceSimulationDay();
                    }
                    loadSimulationState();
                    setMessage(`⚡ Done! ${totalGames} games played over 20 days.`);
                  } catch (error) {
                    setMessage(`❌ Error: ${error}`);
                  }
                  setTimeout(() => setMessage(''), 5000);
                }}
              >
                ⚡ Sim 20 Days
              </button>
            </div>

            {/* Speed Control */}
            <div className="speed-control">
              <h4>Simulation Speed</h4>
              <div className="speed-buttons">
                {(['slow', 'normal', 'fast', 'instant'] as const).map(speed => (
                  <button 
                    key={speed}
                    className={`speed-btn ${simState.speed === speed ? 'active' : ''}`}
                    onClick={async () => {
                      await setSimulationSpeed(speed);
                      loadSimulationState();
                    }}
                  >
                    {speed === 'slow' && '🐢 Slow'}
                    {speed === 'normal' && '🏃 Normal'}
                    {speed === 'fast' && '🚀 Fast'}
                    {speed === 'instant' && '⚡ Instant'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Auto-Tick */}
          <div className="sim-quick-actions">
            <h3>⏱️ Auto-Tick</h3>
            <p>Automatically advance days on a timer. Set it and walk away.</p>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ color: '#ccc' }}>
                Tick every{' '}
                <input
                  type="number"
                  min={1}
                  max={60}
                  value={autoTickMinutes}
                  onChange={e => setAutoTickMinutes(Math.max(1, parseInt(e.target.value) || 1))}
                  disabled={autoTickActive}
                  style={{ width: '60px', padding: '6px 8px', borderRadius: '6px', border: '1px solid #555', background: '#222', color: '#fff', fontSize: '16px', textAlign: 'center' }}
                />{' '}
                minutes
              </label>
              {!autoTickActive ? (
                <button className="action-btn" onClick={() => startAutoTick()} style={{ background: '#2d7d46' }}>
                  ▶️ Start Auto-Tick
                </button>
              ) : (
                <button className="action-btn danger" onClick={stopAutoTick}>
                  ⏹️ Stop Auto-Tick
                </button>
              )}
              {autoTickActive && nextTickIn > 0 && (
                <span style={{ color: '#4ade80', fontSize: '14px' }}>
                  Next tick in {Math.floor(nextTickIn / 60)}:{String(nextTickIn % 60).padStart(2, '0')}
                </span>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="sim-quick-actions">
            <h3>Quick Actions</h3>
            <div className="action-buttons">
              <button 
                className="action-btn"
                onClick={async () => {
                  setMessage('⚡ Simulating to playoffs (Day 161)...');
                  const result = await simulateToPlayoffs();
                  await loadSimulationState();
                  setMessage(`✅ Simulated ${result.daysSimulated} days (${result.gamesPlayed} games) - Playoffs starting!`);
                  setTimeout(() => setMessage(''), 5000);
                }}
              >
                🏃 Simulate to Playoffs
              </button>
              <button 
                className="action-btn"
                onClick={async () => {
                  setMessage('⚡ INSTANT SIMULATION - simulating all remaining games...');
                  const result = await instantSimulate();
                  await loadSimulationState();
                  setMessage(`✅ Instant sim complete: ${result.daysSimulated} days, ${result.gamesPlayed} games`);
                  setTimeout(() => setMessage(''), 5000);
                }}
              >
                ⚡ Instant Sim (Full Season)
              </button>
              <button 
                className="action-btn danger"
                onClick={async () => {
                  if (!confirm('Reset to Day 0? This will archive the current season and clear all schedules/standings.')) return;
                  if (isSupabaseConfigured() && supabase) {
                    // Archive current season standings before clearing
                    const seasonYear = new Date().getFullYear();
                    const { data: currentStandings } = await supabase.from('league_standings').select('*');
                    
                    if (currentStandings && currentStandings.length > 0) {
                      const seasonRecords = currentStandings.map((s: any, idx: number) => ({
                        id: `season_${seasonYear}_${s.team_id || idx}_${Date.now()}`,
                        season_year: seasonYear,
                        league_id: s.league_id,
                        team_id: s.team_id,
                        wins: s.wins || 0,
                        losses: s.losses || 0,
                        runs_scored: s.runs_scored || 0,
                        runs_allowed: s.runs_allowed || 0,
                        division: s.division,
                        conference: s.conference,
                        archived_at: new Date().toISOString(),
                      }));
                      
                      await supabase.from('season_history').upsert(seasonRecords);
                      console.log(`📋 Archived ${seasonRecords.length} standings for Season ${seasonYear}`);
                    }
                    
                    // Clear schedule
                    await supabase.from('league_schedule').delete().neq('id', 'never-match-this');
                    // Reset standings to 0
                    await supabase.from('league_standings').update({
                      wins: 0, losses: 0, runs_scored: 0, runs_allowed: 0
                    }).not('team_id', 'is', null);
                    // Clear per-game stats (current season only)
                    await supabase.from('player_game_stats').delete().neq('id', 'never-match-this');
                    // DO NOT delete player_season_stats — those are career totals
                    // Clear playoffs
                    await supabase.from('playoff_games').delete().neq('id', 'never-match-this');
                    await supabase.from('playoff_series').delete().neq('id', 'never-match-this');
                    await supabase.from('playoff_brackets').delete().neq('id', 'never-match-this');
                    // Reset simulation
                    await supabase.from('simulation_state').upsert({
                      id: 'global', is_running: false, current_day: 0, speed: 'normal',
                      last_updated: new Date().toISOString()
                    });
                  }
                  await loadSimulationState();
                  loadData();
                  setMessage(`🔄 Season archived! Reset to Day 0.`);
                  setTimeout(() => setMessage(''), 3000);
                }}
              >
                🔄 Reset to Day 0
              </button>
            </div>
          </div>

          {message && <div className="message">{message}</div>}
        </div>
      )}

      {/* Generate League Tab */}
      {activeTab === 'generate' && (
        <div className="comm-section">
          <h2>🧪 Generate Test League</h2>
          <p className="section-desc">Create 20 AI teams with players to test the game</p>

          <div className="generate-info">
            <p>This will create:</p>
            <ul>
              <li>20 AI user accounts with 30,000 BALLS each</li>
              <li>20 teams (one per AI account)</li>
              <li>25 players per team (15 hitters, 10 pitchers)</li>
              <li>500 total players created</li>
            </ul>
          </div>

          <button 
            className="generate-btn"
            onClick={async () => {
              const confirm = window.confirm('This will create 20 teams with 500 players in Supabase. Continue?');
              if (!confirm) return;
              
              setMessage('⏳ Generating league in Supabase...');
              
              const teamNames = [
                // American East (6 teams)
                'New York Bombers', 'Boston Brawlers', 'Baltimore Orioles', 'Tampa Bay Rays',
                'Toronto Blue Jays', 'Philadelphia Phillies',
                // American West (6 teams)
                'Houston Astros', 'Seattle Storm', 'Texas Rangers', 'Oakland Athletics',
                'Los Angeles Angels', 'Minnesota Twins',
                // National East (6 teams)
                'Atlanta Braves', 'Miami Marlins', 'Washington Nationals', 'New York Mets',
                'Pittsburgh Pirates', 'Cincinnati Reds',
                // National West (6 teams)
                'Los Angeles Dodgers', 'San Francisco Giants', 'Colorado Rockies', 'Arizona Diamondbacks',
                'San Diego Padres', 'Milwaukee Brewers',
              ];
              
              const conferenceDivisions = [
                { conference: 'American', division: 'East' },
                { conference: 'American', division: 'East' },
                { conference: 'American', division: 'East' },
                { conference: 'American', division: 'East' },
                { conference: 'American', division: 'East' },
                { conference: 'American', division: 'East' },
                { conference: 'American', division: 'West' },
                { conference: 'American', division: 'West' },
                { conference: 'American', division: 'West' },
                { conference: 'American', division: 'West' },
                { conference: 'American', division: 'West' },
                { conference: 'American', division: 'West' },
                { conference: 'National', division: 'East' },
                { conference: 'National', division: 'East' },
                { conference: 'National', division: 'East' },
                { conference: 'National', division: 'East' },
                { conference: 'National', division: 'East' },
                { conference: 'National', division: 'East' },
                { conference: 'National', division: 'West' },
                { conference: 'National', division: 'West' },
                { conference: 'National', division: 'West' },
                { conference: 'National', division: 'West' },
                { conference: 'National', division: 'West' },
                { conference: 'National', division: 'West' },
              ];
              const positions = ['P', 'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'];
              
              // First create the league
              const leagueId = `league_${Date.now()}`;
              const leagueName = 'Major League Baseball 2026';
              
              if (isSupabaseConfigured() && supabase) {
                // Create league in Supabase
                await supabase.from('leagues').insert({
                  id: leagueId,
                  name: leagueName,
                  tier: 1,
                  status: 'active',
                  is_ai_run: true,
                  sim_speed: 5, // minutes between ticks for Cloudflare Worker
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString()
                });
              }
              
              // Create teams and players
              const createdTeams: string[] = [];
              
              for (let i = 0; i < 24; i++) {
                const uuid = crypto.randomUUID();
                const userId = uuid;
                const teamId = uuid;
                const teamName = teamNames[i];
                const confDiv = conferenceDivisions[i];
                const tier = 1; // All Elite tier for testing
                
                createdTeams.push(teamId);
                
                // Create user in Supabase
                if (isSupabaseConfigured() && supabase) {
                  await supabase.from('users').insert({
                    id: userId,
                    username: teamName.replace(' ', '_').toLowerCase(),
                    email: `${teamName.replace(' ', '').toLowerCase()}@example.com`
                  });
                  
                  // Create team
                  const cityExtract = teamName.replace(/ (Bombers|Brawlers|Orioles|Rays|Blue\s*Jays|Phillies|Astros|Storm|Rangers|Athletics|Angels|Twins|Braves|Marlins|Nationals|Mets|Pirates|Reds|Dodgers|Giants|Rockies|Diamondbacks|Padres|Brewers)$/, '').trim();
                  const { error: teamError } = await supabase.from('teams').insert({
                    id: teamId,
                    owner_id: userId,
                    name: teamName,
                    abbreviation: teamName.slice(0, 3).toUpperCase(),
                    city: cityExtract,
                    tier,
                    league_id: leagueId,
                    conference: confDiv.conference,
                    division: confDiv.division,
                    is_ai: true,
                    wins: 0,
                    losses: 0,
                    runs_scored: 0,
                    runs_allowed: 0,
                    revenue: 0,
                    attendance: 0
                  });
                  
                  if (teamError) {
                    console.error('Team insert error:', teamError);
                    setMessage(`❌ Error creating team: ${teamError.message}`);
                    return;
                  }
                }
                
                // Create 25 players per team (small batches with delays)
                let playersCreated = 0;
                const firstNames = ['Mike', 'John', 'Chris', 'Steve', 'Tom', 'David', 'Mark', 'Paul', 'James', 'Ryan'];
                const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez'];
                
                for (let p = 0; p < 25; p++) {
                  const isPitcher = p >= 15;
                  const position = isPitcher ? 'P' : positions[p % positions.length];
                  
                  const overall = 50 + Math.floor(Math.random() * 40);
                  const playerId = `player_${Date.now()}_${i}_${p}`;
                  
                  const playerData: any = {
                    id: playerId,
                    user_id: userId,
                    team_id: teamId,
                    first_name: firstNames[Math.floor(Math.random() * 10)],
                    last_name: lastNames[Math.floor(Math.random() * 10)],
                    position,
                    throwing_hand: Math.random() > 0.5 ? 'R' : 'L',
                    batting_hand: Math.random() > 0.5 ? 'R' : 'L',
                    height: 68 + Math.floor(Math.random() * 10),
                    weight: 170 + Math.floor(Math.random() * 40),
                    age: 22 + Math.floor(Math.random() * 15),
                    overall,
                    is_hitter: !isPitcher,
                    retired: false,
                  };
                  
                  if (isPitcher) {
                    playerData.velocity = overall - 10 + Math.floor(Math.random() * 20);
                    playerData.control = overall - 10 + Math.floor(Math.random() * 20);
                    playerData.movement = overall - 10 + Math.floor(Math.random() * 20);
                    playerData.endurance = overall - 10 + Math.floor(Math.random() * 20);
                  } else {
                    playerData.power = overall - 10 + Math.floor(Math.random() * 20);
                    playerData.contact = overall - 10 + Math.floor(Math.random() * 20);
                    playerData.speed = overall - 10 + Math.floor(Math.random() * 20);
                    playerData.fielding = overall - 10 + Math.floor(Math.random() * 20);
                    playerData.arm = overall - 10 + Math.floor(Math.random() * 20);
                    playerData.discipline = overall - 10 + Math.floor(Math.random() * 20);
                    playerData.endurance = overall - 10 + Math.floor(Math.random() * 20);
                  }
                  
                  if (isSupabaseConfigured() && supabase) {
                    const { error } = await supabase.from('players').insert(playerData);
                    if (error) {
                      console.error(`❌ Player ${p+1} error for ${teamName}:`, error.message);
                      break;
                    } else {
                      playersCreated++;
                    }
                  }
                }
                
                console.log(`✅ ${teamName}: ${playersCreated}/25 players`);
                setMessage(`⏳ Team ${i+1}/24: ${playersCreated} players created...`);
              }
              
              // Create initial simulation state and generate schedule
              if (isSupabaseConfigured() && supabase) {
                await supabase.from('simulation_state').upsert({
                  id: 'global',
                  is_running: false,
                  current_day: 1,
                  speed: 'normal',
                  last_updated: new Date().toISOString()
                });

                // Generate schedule using existing team conference/division assignments
                console.log('📅 Starting schedule generation...');
                const { generateSeasonSchedule } = await import('../engine/ScheduleGenerator');
                
                const { data: leagueTeams } = await supabase
                  .from('teams')
                  .select('id, conference, division')
                  .eq('league_id', leagueId);
                
                console.log(`📅 Found ${leagueTeams?.length || 0} teams for schedule`);
                
                if (leagueTeams && leagueTeams.length === 24) {
                  const teamAssignments = leagueTeams.map((t, idx) => ({
                    teamId: t.id,
                    conference: t.conference as 'American' | 'National',
                    division: t.division as 'East' | 'West',
                    position: idx + 1,
                  }));
                  
                  const games = generateSeasonSchedule(teamAssignments, leagueId);
                  console.log(`📅 Generated ${games.length} games`);
                  
                  if (games.length > 0) {
                    const gameRecords = (games as any[]).map(g => ({
                      league_id: leagueId,
                      game_id: g.game_id,
                      series_id: g.series_id || null,
                      home_team_id: g.home_team_id,
                      away_team_id: g.away_team_id,
                      game_day: g.game_day,
                      game_number: g.game_number,
                      status: 'scheduled',
                      game_date: "",
                    }));
                    
                    console.log(`📅 Inserting ${gameRecords.length} games into league_schedule...`);
                    const { error: scheduleError } = await supabase.from('league_schedule').insert(gameRecords);
                    if (scheduleError) {
                      console.error('❌ Schedule insert error:', JSON.stringify(scheduleError));
                    } else {
                      console.log(`✅ Schedule saved: ${gameRecords.length} games`);
                    }
                  } else {
                    console.log('⚠️ No games generated!');
                  }
                }
                
                // Create standings for all teams
                for (const teamId of createdTeams) {
                  const teamIdx = createdTeams.indexOf(teamId);
                  const confDiv = conferenceDivisions[teamIdx];
                  const { error: standingError } = await supabase.from('league_standings').insert({
                    id: `standing_${leagueId}_${teamId}`,
                    team_id: teamId,
                    wins: 0,
                    losses: 0,
                    runs_scored: 0,
                    runs_allowed: 0,
                    division: confDiv.division,
                    conference: confDiv.conference,
                    league_id: leagueId
                  });
                  if (standingError) {
                    console.error(`❌ Standing error for team ${teamId}:`, standingError.message);
                  }
                }
              }
              
              // Verify what was created
              if (isSupabaseConfigured() && supabase) {
                const { count: teamCount } = await supabase.from('teams').select('*', { count: 'exact', head: true }).eq('league_id', leagueId);
                const { count: playerCount } = await supabase.from('players').select('*', { count: 'exact', head: true }).eq('team_id', createdTeams[0]);
                const { count: scheduleCount } = await supabase.from('league_schedule').select('*', { count: 'exact', head: true }).eq('league_id', leagueId);
                const { count: standingsCount } = await supabase.from('league_standings').select('*', { count: 'exact', head: true }).eq('league_id', leagueId);
                
                console.log(`📊 GENERATE RESULTS: ${teamCount} teams, ${playerCount} players on first team, ${scheduleCount} games, ${standingsCount} standings`);
                setMessage(`✅ League generated! ${teamCount} teams, ~${playerCount} players/team, ${scheduleCount} games scheduled. Check console for details.`);
              } else {
                setMessage('✅ League generated locally!');
              }
            }}
          >
            🚀 Generate League (20 Teams, 500 Players) - Saves to Supabase
          </button>

          {/* Danger Zone */}
          <div className="danger-zone">
            <h3>⚠️ Danger Zone</h3>
            <p className="section-desc">Mass delete for testing. These actions cannot be undone.</p>
            <div className="action-buttons">
              <button 
                className="action-btn danger"
                onClick={async () => {
                  if (!confirm('Delete ALL teams and their data? This clears teams, standings, schedules, and unassigns players.')) return;
                  if (isSupabaseConfigured() && supabase) {
                    await supabase.from('league_schedule').delete().gte('game_day', 0);
                    await supabase.from('league_standings').delete().gte('wins', -1);
                    await supabase.from('players').update({ team_id: null }).gte('age', 0);
                    await supabase.from('teams').delete().gte('wins', -1);
                    await supabase.from('team_queue').delete().gte('status', '');
                  }
                  await updateSimulationState({ current_day: 0 });
                  loadData();
                  loadSimulationState();
                  setMessage('🗑️ All teams deleted');
                  setTimeout(() => setMessage(''), 3000);
                }}
              >
                🗑️ Delete ALL Teams
              </button>
              <button 
                className="action-btn danger"
                onClick={async () => {
                  if (!confirm('Delete ALL leagues? Teams will be unassigned.')) return;
                  if (isSupabaseConfigured() && supabase) {
                    await supabase.from('teams').update({ league_id: null, conference: null, division: null }).gte('wins', -1);
                    await supabase.from('league_schedule').delete().gte('game_day', 0);
                    await supabase.from('league_standings').delete().gte('wins', -1);
                    await supabase.from('playoff_brackets').delete().gte('season_year', 0);
                    await supabase.from('playoff_series').delete().gte('home_wins', -1);
                    await supabase.from('playoff_games').delete().gte('game_day', 0);
                    await supabase.from('leagues').delete().gte('tier', 0);
                  }
                  await updateSimulationState({ current_day: 0 });
                  loadLeagues();
                  loadData();
                  loadSimulationState();
                  setMessage('🗑️ All leagues deleted');
                  setTimeout(() => setMessage(''), 3000);
                }}
              >
                🗑️ Delete ALL Leagues
              </button>
              <button 
                className="action-btn danger"
                onClick={async () => {
                  if (!confirm('Delete ALL players? Cannot be undone.')) return;
                  if (isSupabaseConfigured() && supabase) {
                    const { error } = await supabase.from('players').delete().gte('age', 0);
                    if (error) console.error('Delete players error:', error);
                  }
                  loadData();
                  setMessage('🗑️ All players deleted');
                  setTimeout(() => setMessage(''), 3000);
                }}
              >
                🗑️ Delete ALL Players
              </button>
              <button 
                className="action-btn danger"
                onClick={async () => {
                  if (!confirm('NUKE EVERYTHING? Deletes all teams, leagues, players, schedules, standings, and resets simulation.')) return;
                  if (isSupabaseConfigured() && supabase) {
                    const { error } = await supabase.rpc('nuke_everything');
                    if (error) {
                      console.error('Nuke RPC not available, using manual deletes');
                      // Fallback: delete in order (foreign keys matter)
                      await supabase.from('league_schedule').delete().gte('game_day', 0);
                      await supabase.from('league_standings').delete().gte('wins', -1);
                      await supabase.from('playoff_games').delete().gte('game_day', 0);
                      await supabase.from('playoff_series').delete().gte('home_wins', -1);
                      await supabase.from('playoff_brackets').delete().gte('season_year', 0);
                      await supabase.from('contract_offers').delete().gte('salary', 0);
                      await supabase.from('forge_listings').delete().gte('fee', 0);
                      await supabase.from('forge_breeds').delete().gte('breed_fee', 0);
                      await supabase.from('player_dna').delete().gte('mutation_count', -1);
                      await supabase.from('players').delete().gte('age', 0);
                      await supabase.from('team_queue').delete().gte('status', '');
                      await supabase.from('teams').delete().gte('wins', -1);
                      await supabase.from('leagues').delete().gte('tier', 0);
                    }
                    await supabase.from('simulation_state').upsert({
                      id: 'global', is_running: false, current_day: 0, speed: 'normal',
                      last_updated: new Date().toISOString()
                    });
                  }
                  loadLeagues();
                  loadData();
                  loadSimulationState();
                  setMessage('💣 Everything nuked. Fresh start.');
                  setTimeout(() => setMessage(''), 3000);
                }}
              >
                💣 NUKE EVERYTHING
              </button>
            </div>
          </div>

          {message && <div className="message">{message}</div>}
        </div>
      )}

      {/* Team Queue Tab */}
      {activeTab === 'team-queue' && (
        <div className="comm-section">
          <h2>📋 Team Queue Management</h2>
          <p className="section-desc">Assign teams to leagues during off-season</p>
          
          <div className="queue-redirect">
            <p>Manage teams waiting for league assignment.</p>
            <button 
              className="redirect-btn"
              onClick={() => handleNavigate('team-queue')}
            >
              Open Team Queue →
            </button>
          </div>
        </div>
      )}

      {/* Sim Log Tab */}
      {activeTab === 'season-transition' && (
        <SeasonTransitionWizard
          leagues={leagues}
          supabase={supabase}
          FUNCTIONS_URL={FUNCTIONS_URL}
          onDone={() => { loadLeagues(); loadSimulationState(); }}
          setMessage={setMessage}
        />
      )}

      {activeTab === 'sim-log' && (
        <div className="comm-section">
          <h2>📊 Sim Tick Log</h2>
          <p className="section-desc">Edge function timing — how long each sim day takes</p>

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '20px' }}>
            <button className="action-btn" onClick={loadSimLogs} disabled={simLogLoading}>
              {simLogLoading ? '⏳ Refreshing...' : '🔄 Refresh'}
            </button>
            <span style={{ color: '#888', fontSize: '14px' }}>
              {simLogs.length > 0 ? `Showing last ${simLogs.length} ticks` : 'No logs yet'}
            </span>
          </div>

          {simLogs.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
              <p style={{ fontSize: '18px' }}>🕐 No sim ticks logged yet</p>
              <p style={{ fontSize: '14px' }}>Make sure a league is Active with sim_speed &gt; 0, and the sim is running.</p>
              <p style={{ fontSize: '13px', marginTop: '8px' }}>
                The edge function logs start time → sim day → finish time → duration.
              </p>
            </div>
          ) : (
            <div className="sim-log-table" style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Started At</th>
                    <th>League</th>
                    <th>Day</th>
                    <th>Games</th>
                    <th>Status</th>
                    <th>Duration</th>
                    <th>Summary</th>
                  </tr>
                </thead>
                <tbody>
                  {simLogs.map((log: any, i: number) => (
                    <tr key={log.id} className={
                      log.status === 'running' ? 'sim-log-running' :
                      log.status === 'error' ? 'sim-log-error' :
                      ''
                    }>
                      <td style={{ whiteSpace: 'nowrap', fontSize: '13px' }}>
                        {log.started_at ? new Date(log.started_at).toLocaleTimeString() : '—'}
                      </td>
                      <td style={{ fontSize: '13px' }}>{log.league_name || '—'}</td>
                      <td style={{ textAlign: 'center', fontSize: '13px' }}>{log.day ?? '—'}</td>
                      <td style={{ textAlign: 'center', fontSize: '13px' }}>
                        {log.games_simmed ?? '—'}/12
                      </td>
                      <td>
                        <span className={`sim-log-status ${log.status || 'unknown'}`}>
                          {log.status === 'running' && '🔄 Running'}
                          {log.status === 'success' && '✅ Done'}
                          {log.status === 'error' && '❌ Error'}
                          {!log.status && '❓'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center', fontSize: '14px', fontWeight: log.status === 'success' ? '600' : '400' }}>
                        {log.duration_sec ? `${log.duration_sec}s` : log.status === 'running' ? '⏳ ...' : '—'}
                      </td>
                      <td style={{ fontSize: '12px', color: '#aaa', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {log.error ? log.error : log.summary || ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {simLogs.length > 0 && (
            <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', fontSize: '13px', color: '#888' }}>
              💡 <strong style={{ color: '#ccc' }}>How to read this:</strong>
              <ul style={{ margin: '6px 0 0 0', paddingLeft: '20px' }}>
                <li><strong>Started At</strong> — when the edge function began processing this league</li>
                <li><strong>Games</strong> — games simmed that day (out of 12 max)</li>
                <li><strong>Duration</strong> — total time from start to finish</li>
                <li>If <span style={{ color: '#4ade80' }}>✅ Done in ~Xs</span> then it's working fine. If errors show here, that's your problem.</li>
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Inline economics editor for each league
const LeagueEconomics: React.FC<{ league: any; onSaved: () => void }> = ({ league, onSaved }) => {
  const [expanded, setExpanded] = useState(false);
  const [fee, setFee] = useState(league.entry_fee || 10000);
  const [prizes, setPrizes] = useState<{ position: number; amount: number }[]>(
    league.season_prizes || [
      { position: 1, amount: 50000 },
      { position: 2, amount: 30000 },
      { position: 3, amount: 20000 },
      { position: 4, amount: 15000 },
      { position: 5, amount: 10000 },
      { position: 6, amount: 8000 },
      { position: 7, amount: 5000 },
      { position: 8, amount: 2000 },
    ]
  );

  async function handleSave() {
    if (!isSupabaseConfigured() || !supabase) return;
    await supabase
      .from('leagues')
      .update({ entry_fee: fee, season_prizes: prizes })
      .eq('id', league.id);
    onSaved();
  }

  function updatePrize(idx: number, amount: number) {
    setPrizes(prev => prev.map((p, i) => i === idx ? { ...p, amount } : p));
  }

  function addPrize() {
    setPrizes(prev => [...prev, { position: prev.length + 1, amount: 1000 }]);
  }

  function removePrize(idx: number) {
    setPrizes(prev => prev.filter((_, i) => i !== idx).map((p, i) => ({ ...p, position: i + 1 })));
  }

  const totalPool = prizes.reduce((s, p) => s + p.amount, 0);

  if (!expanded) {
    return (
      <button className="econ-expand-btn" onClick={() => setExpanded(true)}>
        💰 Edit Economics (Fee: {fee.toLocaleString()} | Pool: {totalPool.toLocaleString()}) ▾
      </button>
    );
  }

  return (
    <div className="econ-editor">
      <div className="econ-header">
        <span>💰 Economics</span>
        <button className="econ-collapse" onClick={() => setExpanded(false)}>▲</button>
      </div>
      <div className="econ-row">
        <label>Entry Fee:</label>
        <input type="number" value={fee} onChange={e => setFee(Number(e.target.value))} className="econ-input" />
        <span className="econ-unit">$BALLS</span>
      </div>
      <div className="econ-prizes">
        <label>Prize Pool ({totalPool.toLocaleString()} $BALLS total):</label>
        {prizes.map((p, idx) => (
          <div key={idx} className="econ-prize-row">
            <span className="prize-pos">#{p.position}</span>
            <input type="number" value={p.amount} onChange={e => updatePrize(idx, Number(e.target.value))} className="econ-input" />
            <span className="econ-unit">$BALLS</span>
            <button className="prize-remove" onClick={() => removePrize(idx)}>×</button>
          </div>
        ))}
        <button className="prize-add" onClick={addPrize}>+ Add Position</button>
      </div>
      <button className="econ-save" onClick={handleSave}>Save Economics</button>
    </div>
  );
};

export default CommissionerPage;
